// OPS-3 (2026-05-31) — Bill Intake + Approval Workflow UI.
// Three surfaces, all sharing the split-view + status-engine primitives below:
//   • BillIntakeQueue  — Operations admin queue (list · document preview · coding/routing)
//   • BillReviewQueue  — adjuster review queue (claim-scoped, split-view, approve/audit/…)
//   • ClaimBillsTab    — claim-level bill panel (Add Bill prefilled, stepper, preview)
// Bills live in bill_intake_items (SEPARATE from payments) until a Payment Ready
// transition mints the payments row server-side. "Do not make users code bills blind":
// every review surface shows the document next to the metadata/actions.
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { apiFetch, fmt, can, canOnClaim, getUser } from "../api";
import { PageHeader, Spinner, StatusPill, SummaryRibbon, RibbonCell } from "./ui";  // UI-SWEEP-OPS / UI-RIBBON-1
import PayeeProfileModal from "./PayeeProfileModal";

// ─── vocabulary (mirrors payment_calc.py + the server _BILL_TX tables) ───────
const CATS = [
  { k: "category_medical", label: "Medical" },
  { k: "category_bill_review", label: "Bill Review" },
  { k: "category_transport", label: "Transport" },
  { k: "category_maintenance", label: "Maintenance" },
  { k: "category_legal", label: "Legal" },
  { k: "category_adjusting", label: "Adjusting" },
  { k: "category_uew", label: "UEW" },
  { k: "category_settlement", label: "Settlement" },
  { k: "category_reimburse", label: "Reimburse" },
  { k: "category_other_expense", label: "Other" },
];
const FUNDING = [
  { v: "", label: "—" },
  { v: "trust", label: "Trust" },
  { v: "owner", label: "Owner" },
  { v: "assured", label: "Assured" },
  { v: "underwriters", label: "Underwriters" },
  { v: "other", label: "Other" },
];
const PAYMENT_METHODS = [
  { v: "", label: "—" }, { v: "paper_check", label: "Paper check" }, { v: "ach", label: "ACH" },
  { v: "venmo", label: "Venmo" }, { v: "credit_card", label: "Credit card" },
  { v: "other", label: "Other" }, { v: "tbd", label: "Don't know / TBD" },
];
const FUNDING_DISPOSITIONS = [
  { v: "", label: "—" }, { v: "trust_direct", label: "Paid from claim/trust funds" },
  { v: "wnw_reimbursable", label: "WNW paid — reimbursable" },
  { v: "wnw_nonreimbursable", label: "WNW paid — non-reimbursable" },
  { v: "tbd", label: "Unknown / TBD" },
];
const STATUS_COLOR = {
  "Received": "#64748b", "Needs Claim": "#d97706", "Needs Coding": "#d97706",
  "Routed": "#2563eb", "Adjuster Review": "#2563eb", "Audit": "#7c3aed",
  "Approved": "#16a34a", "Accounting Review": "#0d9488", "Payment Ready": "#0891b2",
  "Queued in QBO": "#1d4ed8", "Paid": "#0f766e",
  "Hold": "#d97706", "Rejected": "#dc2626", "Request Info": "#d97706", "Voided": "#64748b",
};
// Display-only relabels — backend status strings are unchanged. Adjuster-owned
// coding (2026-06-23): "Needs Coding" reads as "Needs Routing"; "Request Info"
// reads as "Needs Add'l Info".
const STATUS_DISPLAY = { "Needs Coding": "Needs Routing", "Request Info": "Needs Add'l Info", "Approved": "Adjuster Approved" };
const dispStatus = (s) => STATUS_DISPLAY[s] || s;
// Queue chip → underlying statuses (grouped). "All" = active (excludes terminal).
const CHIP_GROUPS = [
  { label: "All", statuses: null },
  { label: "Needs Claim", statuses: ["Received", "Needs Claim"] },
  { label: "Needs Routing", statuses: ["Needs Coding"] },
  { label: "Needs Add'l Info", statuses: ["Request Info", "Hold"] },
  { label: "Adjuster Review", statuses: ["Routed", "Adjuster Review"] },
  { label: "Audit", statuses: ["Audit"] },
  { label: "Adjuster Approved", statuses: ["Approved", "Accounting Review"] },
  { label: "Payment Ready", statuses: ["Payment Ready"] },
  { label: "Queued in QBO", statuses: ["Queued in QBO"] }, // none yet — Wave 3 (QBO handoff)
];
const ACTIVE_EXCLUDE = ["Paid", "Rejected", "Voided"]; // historical; reachable via Payment History
// WS0-B H4: statuses whose reversal is Void, never Delete — DELETE /bills/{id} returns 409 for
// these (main.delete_bill / _BILL_MONEY_STATUSES). Keep in step with that tuple.
const NO_DELETE_STATUSES = ["Approved", "Accounting Review", "Payment Ready", "Queued in QBO", "Paid"];
const STEPS = ["Received", "Needs Coding", "Routed", "Adjuster Review", "Approved", "Accounting Review", "Payment Ready", "Queued in QBO", "Paid"];
// WS0-B H14/F-13: "Voided" was absent, so BillStepper fell back to index 0 and drew a voided
// bill as if it were at step 1 of 9. A void happens at/after Approved -> branch at 4.
const BRANCH_AT = { "Needs Claim": 0, "Audit": 3, "Hold": 2, "Request Info": 2, "Rejected": 3, "Voided": 4 };
// Wave 3: the single next action per status -> drives the "Next step" cue.
const NEXT_STEP = {
  "Received":          { t: "Tag this bill to a claim.", tone: "act" },
  "Needs Claim":       { t: "Tag this bill to a claim.", tone: "act" },
  "Needs Coding":      { t: "Assign a reviewer and route to the adjuster (coding optional — the adjuster owns it).", tone: "act" },
  "Routed":            { t: "Awaiting the adjuster — opens into review when they pick it up.", tone: "wait" },
  "Adjuster Review":   { t: "Adjuster: code, then approve, send to audit, or kick back to Ops.", tone: "act" },
  "Approved":          { t: "Send to Accounting Review.", tone: "act" },
  "Audit":             { t: "Under audit review.", tone: "wait" },
  "Accounting Review": { t: "Accounting must approve before Payment Ready.", tone: "act" },
  "Payment Ready":     { t: "Ready to pay \u2014 queue to QuickBooks or mark paid.", tone: "act" },
  "Queued in QBO":     { t: "Queued in QuickBooks \u2014 awaiting payment confirmation.", tone: "wait" },
  "Paid":              { t: "Paid \u2014 complete.", tone: "done" },
  "Hold":              { t: "On hold \u2014 resume when ready.", tone: "wait" },
  "Request Info":      { t: "Awaiting requested information.", tone: "wait" },
  "Rejected":          { t: "Rejected.", tone: "term" },
  "Voided":            { t: "Voided \u2014 no payment will be made.", tone: "term" },
};
// Allowed transitions per current status — an EXACT mirror of the server's `_BILL_TX`
// (backend/main.py). WS0-B H14/F-13: the comment used to claim this and it was false in five
// places (four missing edges, a missing "Voided" key, and `Approved → Payment Ready` which
// LD-REV-002 removed because LD-BILL-003's Accounting Review is a hard gate). Nothing tested
// the claim. A structural test now asserts these two literals are equal
// (backend/tests/test_structural.py) — if you edit one, edit the other in the same change.
// Note: WHICH of these a given surface offers is decided by the per-surface filter in
// WorkflowCommandCenter (Ops owns intake/routing; Accounting actions live in the Accounting
// Bill Queue), not by trimming this table.
const TX = {
  "Received": ["Needs Claim", "Needs Coding"],
  "Needs Claim": ["Needs Coding"],
  "Needs Coding": ["Routed"],
  "Routed": ["Adjuster Review", "Hold", "Request Info"],
  "Adjuster Review": ["Approved", "Audit", "Hold", "Request Info", "Rejected"],
  "Audit": ["Adjuster Review", "Approved"],
  "Approved": ["Accounting Review", "Adjuster Review", "Needs Coding", "Voided"],
  "Accounting Review": ["Payment Ready", "Approved", "Adjuster Review", "Needs Coding", "Hold", "Request Info", "Voided"],
  "Payment Ready": ["Queued in QBO", "Paid"],
  "Queued in QBO": ["Paid", "Payment Ready"],
  "Hold": ["Adjuster Review", "Routed"],
  "Request Info": ["Adjuster Review", "Routed"],
  "Paid": [], "Rejected": [], "Voided": [],
};
// to_status → button presentation. needsReason prompts an inline reason field.
const ACT = {
  "Needs Coding": { label: "Mark Coding", color: "#64748b" },
  "Routed": { label: "Route to Adjuster/Claim", color: "#2563eb", admin: true },
  "Adjuster Review": { label: "Begin Review", color: "#2563eb", claim: true },
  "Approved": { label: "Approve", color: "#16a34a", claim: true },
  "Audit": { label: "Send to Audit", color: "#7c3aed", claim: true, needsReason: true },
  "Hold": { label: "Hold", color: "#d97706", claim: true, needsReason: true },
  "Request Info": { label: "Needs Add'l Info", color: "#d97706", claim: true, needsReason: true },
  "Rejected": { label: "Reject", color: "#dc2626", claim: true, needsReason: true },
  "Accounting Review": { label: "Send to Accounting Review", color: "#0d9488", admin: true },
  "Payment Ready": { label: "Mark Payment Ready", color: "#0891b2", admin: true },
  "Queued in QBO": { label: "Queue in QBO", color: "#1d4ed8", admin: true },
  "Paid": { label: "Mark Paid", color: "#0f766e", admin: true },
  // WS0-B H14/F-13: `Voided` had no ACT entry, so a Void button would have rendered as a
  // bare blue "Voided". needsReason matches the Accounting queue's confirm and LD-REV-011's
  // "mandatory reason" direction (the un-void back-edge itself ships in WS2).
  "Voided": { label: "Void", color: "#b45309", admin: true, needsReason: true },
};
// contextual relabel for ambiguous targets
const actLabel = (from, to) => {
  if (to === "Adjuster Review" && (from === "Audit" || from === "Approved")) return "Return to Review";
  if (to === "Adjuster Review" && (from === "Hold" || from === "Request Info")) return "Resume Review";
  if (to === "Routed" && (from === "Hold" || from === "Request Info")) return "Send Back to Routing";
  return ACT[to]?.label || to;
};
const money = (n) => (n == null || n === "" ? "—" : "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
// A bill is an "exception" when it can't progress unattended: no claim tagged,
// or parked in a Hold / Request-Info branch.
const isException = (b) => !b.claim_id || ["Hold", "Request Info"].includes(b.current_status);

// ─── shared primitives ───────────────────────────────────────────────────────
export function BillStatusChip({ status, small }) {
  const c = STATUS_COLOR[status] || "#64748b";
  return (
    <span style={{ display: "inline-block", background: `${c}1a`, color: c, border: `1px solid ${c}55`,
      borderRadius: 20, padding: small ? "1px 8px" : "2px 10px", fontSize: small ? 10.5 : 11.5, fontWeight: 700, whiteSpace: "nowrap" }}>
      {dispStatus(status)}
    </span>
  );
}

// Compact one-row track: green ✓ for done, an enlarged haloed dot for "here",
// hollow gray for future — connectors stretch to fill the column (no wrap). A
// "Step N of 9 · Current" line names the position. Obvious where we are + were.
function BillStepper({ status }) {
  const branch = !STEPS.includes(status);
  const idx = branch ? (BRANCH_AT[status] ?? 0) : STEPS.indexOf(status);
  const failed = status === "Rejected";
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
        {STEPS.map((s, i) => {
          const done = i < idx, here = i === idx && !branch;
          const c = failed && i >= idx ? "#dc2626" : done ? "#16a34a" : here ? (STATUS_COLOR[s] || "#2563eb") : "#cbd5e1";
          return (
            <div key={s} style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? "1 1 auto" : "0 0 auto", minWidth: 0 }}>
              <div title={dispStatus(s)} style={{ flexShrink: 0, width: here ? 16 : 13, height: here ? 16 : 13, borderRadius: "50%",
                background: (done || here) ? c : "#fff", border: `2px solid ${c}`, color: "#fff", fontSize: 9, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center", boxShadow: here ? `0 0 0 3px ${c}33` : "none" }}>
                {done ? "✓" : ""}
              </div>
              {i < STEPS.length - 1 && <div style={{ flex: 1, height: 2, minWidth: 6, background: i < idx ? "#16a34a" : "#cbd5e1" }} />}
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 10.5, marginTop: 6, color: "var(--text3)" }}>
        {branch
          ? <BillStatusChip status={status} small />
          : <>Step {idx + 1} of {STEPS.length} · <b style={{ color: STATUS_COLOR[status] || "var(--navy)" }}>{dispStatus(status)}</b></>}
      </div>
    </div>
  );
}

// Disclosure primitive — a clickable header that collapses its children. Used
// to keep History / Audit / Accounting-details out of the way until needed.
function Collapsible({ title, badge, defaultOpen = false, tone, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: "1px solid var(--border)", marginTop: 12, paddingTop: 10 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", userSelect: "none" }}>
        <span style={{ fontSize: 10, color: "var(--text3)", width: 10 }}>{open ? "▾" : "▸"}</span>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: tone || "var(--text3)" }}>{title}</span>
        {badge != null && <span style={{ fontSize: 10, fontWeight: 700, color: tone || "var(--text3)" }}>{badge}</span>}
      </div>
      {open && <div style={{ marginTop: 8 }}>{children}</div>}
    </div>
  );
}

// Document preview pane. Fetches the bill document as a blob (auth header
// required, so no plain <img src>), renders inline by mime, falls back to a link.
function BillDocPreview({ bill, height = 460 }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setUrl(null); setErr(false);
    if (!bill || !bill.has_document) return;
    let dead = false, obj = null;
    apiFetch(`/bills/${bill.id}/document`, { raw: true })
      .then(r => { if (!r.ok) throw new Error("doc"); return r.blob(); })
      .then(b => { if (dead) return; obj = URL.createObjectURL(b); setUrl(obj); })
      .catch(() => { if (!dead) setErr(true); });
    return () => { dead = true; if (obj) URL.revokeObjectURL(obj); };
  }, [bill?.id, bill?.has_document]);

  const frame = { border: "1px solid var(--border)", borderRadius: 8, background: "#f8fafc", height, overflow: "hidden" };
  const center = { ...frame, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text3)", fontSize: 13, textAlign: "center", padding: 16 };
  if (!bill || !bill.has_document) return <div style={center}>No document attached.<br /><span style={{ fontSize: 11 }}>Bill metadata only.</span></div>;
  if (err) return <div style={center}>Couldn't load preview.<br /><a href={`/api/bills/${bill.id}/document`} target="_blank" rel="noreferrer">Open document ↗</a></div>;
  if (!url) return <Spinner label="Loading document…" />;
  const mime = bill.mime_type || "";
  if (mime.startsWith("image/")) return <div style={{ ...frame, display: "flex", alignItems: "center", justifyContent: "center" }}><img src={url} alt={bill.original_filename || "bill"} style={{ maxWidth: "100%", maxHeight: height, objectFit: "contain" }} /></div>;
  if (mime === "application/pdf") return <iframe title="bill" src={url} style={{ ...frame, width: "100%", display: "block" }} />;
  return <div style={center}><a href={url} download={bill.original_filename || "bill"}>Download {bill.original_filename || "document"} ↓</a></div>;
}

// Inline reason capture + confirm for an action button.
function ActionButton({ from, to, onGo, busy }) {
  const meta = ACT[to] || { label: to, color: "#2563eb" };
  const [armed, setArmed] = useState(false);
  const [reason, setReason] = useState("");
  if (meta.needsReason && armed) {
    return (
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <input autoFocus value={reason} onChange={e => setReason(e.target.value)} placeholder={`Reason for ${actLabel(from, to)}…`}
          style={{ fontSize: 12, padding: "5px 8px", border: "1px solid var(--border)", borderRadius: 5, minWidth: 200 }} />
        <button className="btn btn-sm" disabled={busy} onClick={() => { onGo(to, reason); setArmed(false); setReason(""); }}
          style={{ background: meta.color, color: "#fff", borderColor: meta.color }}>Confirm</button>
        <button className="btn btn-sm btn-outline" onClick={() => setArmed(false)}>Cancel</button>
      </div>
    );
  }
  return (
    <button className="btn btn-sm" disabled={busy}
      onClick={() => (meta.needsReason ? setArmed(true) : onGo(to, null))}
      style={{ background: meta.color, color: "#fff", borderColor: meta.color }}>
      {actLabel(from, to)}
    </button>
  );
}

// ─── Add Bill modal (upload + metadata) ──────────────────────────────────────
export function AddBillModal({ claimId = null, claimLabel = null, initialFile = null, onClose, onSaved }) {
  const [form, setForm] = useState({ payee: "", amount: "", invoice_number: "", invoice_date: "", notes: "" });
  const [file, setFile] = useState(initialFile || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      if (claimId) fd.append("claim_id", String(claimId));
      ["payee", "invoice_number", "invoice_date", "notes"].forEach(k => form[k] && fd.append(k, form[k]));
      if (form.amount !== "") fd.append("amount", String(form.amount));
      fd.append("source", claimId ? "claim_submit" : "admin_upload");
      const res = await apiFetch("/bills", { raw: true, method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      const b = await res.json();
      onSaved && onSaved(b);
      onClose();
    } catch (e) { setErr(String(e.message || e).slice(0, 300)); } finally { setBusy(false); }
  };

  return (
    <div style={modalWrap} onMouseDown={onClose}>
      <div style={{ ...modalBox, width: 480 }} onMouseDown={e => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--navy)", marginBottom: 4 }}>Add Bill</div>
        <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 14 }}>
          {claimLabel ? <>For claim <span className="mono">{claimLabel}</span></> : "Unassigned — tag to a claim in the intake queue."}
        </div>
        <label style={lbl}>Document (PDF / image, ≤25 MB)</label>
        <input type="file" accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.webp,.gif" onChange={e => setFile(e.target.files?.[0] || null)} style={{ marginBottom: 12, fontSize: 12 }} />
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 2 }}><label style={lbl}>Payee</label><input value={form.payee} onChange={e => up("payee", e.target.value)} style={inp} /></div>
          <div style={{ flex: 1 }}><label style={lbl}>Amount</label><input type="number" step="0.01" value={form.amount} onChange={e => up("amount", e.target.value)} style={inp} /></div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <div style={{ flex: 1 }}><label style={lbl}>Invoice #</label><input value={form.invoice_number} onChange={e => up("invoice_number", e.target.value)} style={inp} /></div>
          <div style={{ flex: 1 }}><label style={lbl}>Invoice Date</label><input type="date" value={form.invoice_date} onChange={e => up("invoice_date", e.target.value)} style={inp} /></div>
        </div>
        <label style={{ ...lbl, marginTop: 8 }}>Notes</label>
        <textarea value={form.notes} onChange={e => up("notes", e.target.value)} rows={2} style={{ ...inp, resize: "vertical" }} />
        {err && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 8 }}>{err}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-blue" disabled={busy} onClick={submit}>{busy ? "Saving…" : "Add Bill"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── coding + routing panel (admin) ──────────────────────────────────────────
function PayeeChips({ rd }) {
  if (!rd) return null;
  if (!rd.has_profile) return <span style={{ fontSize: 10, color: "#b45309", background: "#fef3c7", borderRadius: 4, padding: "1px 6px" }}>no payee profile</span>;
  const chips = [["W9", rd.w9_ok], ["Addr", rd.address_ok], ["Legal", rd.legal_name_ok], ["Pay", rd.payment_pref_ok], ["QBO", rd.qbo_vendor_ok]];
  return (
    <span style={{ display: "inline-flex", gap: 3, flexWrap: "wrap" }}>
      {rd.vetting_status === "pending" && <span title="New payee — awaiting admin/accounting vetting" style={{ fontSize: 10, color: "#92400e", background: "#fef3c7", borderRadius: 4, padding: "1px 6px", fontWeight: 700 }}>NEEDS VETTING</span>}
      {rd.vetting_status === "rejected" && <span style={{ fontSize: 10, color: "#fff", background: "#991b1b", borderRadius: 4, padding: "1px 6px", fontWeight: 700 }}>REJECTED</span>}
      {rd.payment_hold && <span style={{ fontSize: 10, color: "#fff", background: "#dc2626", borderRadius: 4, padding: "1px 6px" }}>HOLD</span>}
      {chips.map(([label, ok]) => (
        <span key={label} title={label + (ok ? " ok" : " missing")} style={{ fontSize: 10, borderRadius: 4, padding: "1px 6px", background: ok ? "#dcfce7" : "#fee2e2", color: ok ? "#166534" : "#991b1b" }}>{label}{ok ? " ✓" : " ✗"}</span>
      ))}
    </span>
  );
}

function CodingPanel({ bill, surface, onSaved, codingRef }) {
  const blank = useCallback(() => {
    const f = { claim_id: bill.claim_id || "", payee: bill.payee || "", amount: bill.amount ?? "", total_bill_amount: bill.total_bill_amount ?? "",
      invoice_number: bill.invoice_number || "", invoice_date: (bill.invoice_date || "").slice(0, 10),
      description: bill.description || "", due_date: (bill.due_date || "").slice(0, 10),
      date_of_service: (bill.date_of_service || "").slice(0, 10),
      service_date_type: bill.service_date_type || "single", service_date_end: (bill.service_date_end || "").slice(0, 10),
      service_dates: (bill.service_dates || []).map(d => (d || "").slice(0, 10)),
      funding_source: bill.funding_source || "",
      payer: bill.payer || "", assigned_adjuster_id: bill.assigned_adjuster_id || "", notes: bill.notes || "",
      payment_method: bill.payment_method || "", funding_disposition: bill.funding_disposition || "",
      payee_company_id: bill.payee_company_id || "", payee_contact_id: bill.payee_contact_id || "", payee_claimant_id: bill.payee_claimant_id || "",
      funding_source_id: bill.funding_source_id || "", payment_mechanism_id: bill.payment_mechanism_id || "",
      payor_type: bill.payor_type || "", payor_trust_account_id: bill.payor_trust_account_id || "", check_stock_id: bill.check_stock_id || "" };
    return f;
  }, [bill]);
  const [form, setForm] = useState(blank);
  const [cats, setCats] = useState([]);
  const [allocs, setAllocs] = useState(() => (bill.allocations || []).map(a => ({ category_id: a.category_id, amount: a.amount })));
  const [claimQ, setClaimQ] = useState("");
  const [claimHits, setClaimHits] = useState([]);
  const [claimLabel, setClaimLabel] = useState(bill.claim_number || "");
  const [payeeQ, setPayeeQ] = useState("");
  const [payeeHits, setPayeeHits] = useState([]);
  const [payeeRdy, setPayeeRdy] = useState(null);
  const [fundingSources, setFundingSources] = useState([]);
  const [mechanisms, setMechanisms] = useState([]);
  const [users, setUsers] = useState([]);
  const [trustAccounts, setTrustAccounts] = useState([]);
  const [trustMode, setTrustMode] = useState("none");
  const [claimTrustAcct, setClaimTrustAcct] = useState("");
  const [trustBal, setTrustBal] = useState(null);
  const [multiCat, setMultiCat] = useState(false);
  const [payeeModal, setPayeeModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    if (!bill.claim_id) { setTrustBal(null); return; }
    apiFetch(`/claims/${bill.claim_id}/trust-balances`).then(setTrustBal).catch(() => setTrustBal(null));
  }, [bill.claim_id]);
  useEffect(() => { setForm(blank()); setClaimLabel(bill.claim_number || ""); setClaimQ(""); setClaimHits([]); }, [bill.id, blank, bill.claim_number]);
  // Scope G: trust accounts (+ their checkstocks) and the claim's trust config.
  useEffect(() => { apiFetch("/trust-accounts").then(d => setTrustAccounts(d || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!bill.claim_id) { setTrustMode("none"); setClaimTrustAcct(""); return; }
    apiFetch(`/claims/${bill.claim_id}/trust-config`)
      .then(d => { setTrustMode(d.trust_mode || "none"); setClaimTrustAcct(d.trust_account_id || ""); })
      .catch(() => {});
  }, [bill.claim_id]);
  useEffect(() => { apiFetch("/users/assignable").then(d => setUsers(d || [])).catch(() => {}); }, []);
  useEffect(() => { apiFetch("/funding-sources").then(d => setFundingSources(d.funding_sources || [])).catch(() => {}); apiFetch("/payment-mechanisms").then(d => setMechanisms(d.payment_mechanisms || [])).catch(() => {}); }, []);
  useEffect(() => { const al = (bill.allocations || []).map(a => ({ category_id: a.category_id, amount: a.amount })); setAllocs(al); setMultiCat(al.length > 1); }, [bill.id]);
  useEffect(() => { const bl = bill.business_line || "P&I"; apiFetch(`/payment-categories?business_line=${encodeURIComponent(bl)}`).then(d => setCats(d || [])).catch(() => setCats([])); }, [bill.business_line]);
  useEffect(() => {
    if (!claimQ || claimQ.length < 2) { setClaimHits([]); return; }
    const t = setTimeout(() => apiFetch(`/claims?search=${encodeURIComponent(claimQ)}&limit=8`).then(d => setClaimHits(d || [])).catch(() => {}), 220);
    return () => clearTimeout(t);
  }, [claimQ]);
  useEffect(() => {
    if (!payeeQ || payeeQ.length < 2) { setPayeeHits([]); return; }
    const t = setTimeout(() => apiFetch(`/payees/search?q=${encodeURIComponent(payeeQ)}&limit=8`).then(d => setPayeeHits(d.results || [])).catch(() => {}), 220);
    return () => clearTimeout(t);
  }, [payeeQ]);
  useEffect(() => {
    const cid = bill.payee_company_id, ctid = bill.payee_contact_id;
    if (cid) apiFetch(`/payees/entity/company/${cid}`).then(d => setPayeeRdy(d.readiness)).catch(() => setPayeeRdy(null));
    else if (ctid) apiFetch(`/payees/entity/contact/${ctid}`).then(d => setPayeeRdy(d.readiness)).catch(() => setPayeeRdy(null));
    else setPayeeRdy(null);
  }, [bill.id, bill.payee_company_id, bill.payee_contact_id]);
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const amt = parseFloat(form.amount) || 0;
  const allocSum = allocs.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
  const coded = allocs.some(a => a.category_id);                       // a category is chosen
  const mismatch = multiCat && coded && Math.abs(allocSum - amt) > 0.005;  // split must total the amount
  const catById = {}; cats.forEach(c => { catById[c.id] = c; });
  const catOptions = cats.filter(c => !c.parent_id).map(p => {
    const subs = cats.filter(c => c.parent_id === p.id);
    return subs.length === 0
      ? <option key={p.id} value={p.id}>{p.label}</option>
      : <optgroup key={p.id} label={p.label}><option value={p.id}>{p.label} (whole)</option>{subs.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}</optgroup>;
  });
  const addAlloc = () => setAllocs(a => [...a, { category_id: "", amount: "" }]);
  const upAlloc = (i, k, v) => setAllocs(a => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const rmAlloc = (i) => setAllocs(a => a.filter((_, j) => j !== i));

  const save = async ({ silent } = {}) => {
    setBusy(true); setMsg(null);
    try {
      const body = {};
      ["claim_id", "payee", "invoice_number", "invoice_date", "description", "due_date", "date_of_service", "service_date_type", "service_date_end", "payer", "assigned_adjuster_id", "notes", "payment_method"].forEach(k => {
        if (form[k] !== "" && form[k] != null) body[k] = (k === "claim_id" || k === "assigned_adjuster_id") ? parseInt(form[k]) : form[k];
      });
      body.amount = form.amount === "" ? null : parseFloat(form.amount);
      body.total_bill_amount = form.total_bill_amount === "" ? null : parseFloat(form.total_bill_amount);
      // Multiple-DOS list (Chunk 5): send the explicit list when in 'multiple' mode (else clear it).
      body.service_dates = form.service_date_type === "multiple" ? (form.service_dates || []).filter(Boolean) : [];
      body.payee_company_id = form.payee_company_id ? parseInt(form.payee_company_id) : null;
      body.payee_contact_id = form.payee_contact_id ? parseInt(form.payee_contact_id) : null;
      body.payee_claimant_id = form.payee_claimant_id ? parseInt(form.payee_claimant_id) : null;
      body.payment_mechanism_id = form.payment_mechanism_id ? parseInt(form.payment_mechanism_id) : null;
      // Payor model: the adjuster picks ONLY the payor (+ funding method for trust).
      // The trust account + checkstock are set by Accounting in the Bill Queue, so the
      // adjuster save does NOT touch payor_trust_account_id / check_stock_id.
      body.payor_type = form.payor_type || null;
      body.funding_source = { trust: "trust", insured: "assured", underwriter: "underwriters" }[form.payor_type] || null;
      await apiFetch(`/bills/${bill.id}`, { method: "PATCH", body });
      // Single category (the default) gets the whole amount; split mode sends the entered lines.
      const lines = multiCat
        ? allocs.filter(a => a.category_id && a.amount !== "" && a.amount != null && parseFloat(a.amount) !== 0)
                .map(a => ({ category_id: parseInt(a.category_id), amount: parseFloat(a.amount) }))
        : ((allocs[0] && allocs[0].category_id) ? [{ category_id: parseInt(allocs[0].category_id), amount: amt }] : []);
      const updated = await apiFetch(`/bills/${bill.id}/allocations`, { method: "PUT", body: { allocations: lines } });
      // silent = called by the Command Center before a transition: persist, but
      // don't reload/flash "Saved." (the transition reloads once when it lands).
      if (!silent) { setMsg("Saved."); onSaved && onSaved(updated); }
    } catch (e) { setMsg(String(e.message || e).slice(0, 200)); if (silent) throw e; } finally { setBusy(false); }
  };

  const transition = async (to, reason, savedFirst) => {
    const upd = savedFirst || bill;
    await apiFetch(`/bills/${bill.id}/transition`, { method: "POST", body: { to_status: to, reason } });
  };
  // Route chain (Kuhrt 2026-06-27): two clicks — tag a claim AND/OR an adjuster, then
  // route. Coding is NOT required. We persist whatever's typed, then walk to Routed using
  // the bill's FRESH status (tagging a claim auto-advances Needs Claim→Needs Coding on the
  // server, so the old code's stale-status path tried an illegal self-transition).
  const routeToAdjuster = async () => {
    if (!form.claim_id && !form.assigned_adjuster_id) { setMsg("Assign a claim and/or an adjuster before routing."); return; }
    if (mismatch) { setMsg("Category splits must equal the amount."); return; }
    setBusy(true); setMsg(null);
    try {
      await save({ silent: true });                                   // persist typed fields (coding optional)
      const fresh = await apiFetch(`/bills/${bill.id}`);              // fresh status after any auto-advance
      let status = (fresh && fresh.current_status) || bill.current_status;
      const step = async (to) => { const r = await apiFetch(`/bills/${bill.id}/transition`, { method: "POST", body: { to_status: to } }); status = (r && r.current_status) || to; };
      if (status === "Received" || status === "Needs Claim") await step("Needs Coding");
      if (status === "Needs Coding") await step("Routed");
      setMsg("Routed."); onSaved && onSaved();
    } catch (e) { setMsg(String(e.message || e).slice(0, 200)); throw e; } finally { setBusy(false); }
  };
  // Branch actions (kick back to Ops / disclaim) — published to the Command
  // Center via codingRef so there's ONE action surface, not two.
  const kickback = async () => {
    const reason = window.prompt("Kick back to Operations — reason (e.g. 'not for this file'):");
    if (!reason || !reason.trim()) return;
    setBusy(true); setMsg(null);
    try { const u = await apiFetch(`/bills/${bill.id}/kickback`, { method: "POST", body: { reason: reason.trim() } }); setMsg("Kicked back to Ops."); onSaved && onSaved(u); }
    catch (e) { setMsg(String(e.message || e).slice(0, 200)); } finally { setBusy(false); }
  };
  const disclaim = async () => {
    if (!window.confirm("Return this bill to Operations? It leaves your queue and stays 'Needs Claim' for Ops to reassign.")) return;
    setBusy(true); setMsg(null);
    try { await apiFetch(`/bills/${bill.id}/disclaim`, { method: "POST", body: {} }); onSaved && onSaved(); }
    catch (e) { setMsg(String(e.message || e)); } finally { setBusy(false); }
  };
  // Publish the bill's actions so the Command Center can drive them (fresh
  // closures each render — refs are exempt from the render-purity rule here).
  if (codingRef) codingRef.current = { save, routeToAdjuster, kickback, disclaim };

  const saveTrustConfig = async (mode, acctId) => {
    setTrustMode(mode); setClaimTrustAcct(acctId || "");
    if (!bill.claim_id) return;
    try { await apiFetch(`/claims/${bill.claim_id}/trust-config`, { method: "PATCH", body: { trust_mode: mode, trust_account_id: acctId ? parseInt(acctId) : null } }); }
    catch (e) { setMsg(String(e.message || e).slice(0, 150)); }
  };
  // Effective trust account for a trust-paid bill = explicit override, else the claim default.
  const effTrustAcct = form.payor_type === "trust" ? (form.payor_trust_account_id || claimTrustAcct) : "";
  const checkStocks = (trustAccounts.find(t => String(t.id) === String(effTrustAcct)) || {}).check_stocks || [];
  const addCheckStock = async () => {
    if (!effTrustAcct) return;
    const name = window.prompt("New checkstock name for this trust account:");
    if (!name || !name.trim()) return;
    try {
      const cs = await apiFetch("/check-stocks", { method: "POST", body: { trust_account_id: parseInt(effTrustAcct), name: name.trim() } });
      const d = await apiFetch("/trust-accounts"); setTrustAccounts(d || []);
      up("check_stock_id", String(cs.id));
    } catch (e) { setMsg(String(e.message || e).slice(0, 150)); }
  };
  const quickCreatePayee = async () => {
    const name = (form.payee || "").trim();
    if (!name) return;
    setBusy(true); setMsg(null);
    try {
      const r = await apiFetch("/payees/quick", { method: "POST", body: { name } });
      up("payee", r.name || name);
      up("payee_company_id", r.kind === "company" ? r.entity_id : "");
      up("payee_contact_id", r.kind === "contact" ? r.entity_id : "");
      up("payee_claimant_id", "");
      setPayeeRdy(r.readiness); setPayeeHits([]); setPayeeQ("");
    } catch (e) { setMsg(String(e.message || e).slice(0, 150)); } finally { setBusy(false); }
  };
  const canManage = can("manage_bill_intake") || surface === "claim" || surface === "review";
  const SECH = { fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--text3)", margin: "9px 0 3px", borderBottom: "1px solid var(--border)", paddingBottom: 3 };
  return (
    <div style={{ fontSize: 12 }}>
      <div style={{ fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>Coding & Routing</div>
      {/* claim tag */}
      <div style={SECH}>Claim</div>
      {form.claim_id ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span className="mono" style={{ color: "var(--blue)", fontWeight: 700 }}>{claimLabel || `#${form.claim_id}`}</span>
          {canManage && <button className="btn btn-sm btn-outline" onClick={() => { up("claim_id", ""); setClaimLabel(""); }}>Change</button>}
        </div>
      ) : (
        <div style={{ position: "relative", marginBottom: 8 }}>
          <input value={claimQ} onChange={e => setClaimQ(e.target.value)} placeholder="Search claim # / insured / claimant… (required)" style={{ ...inp, borderColor: "#dc2626" }} disabled={!canManage} />
          {claimHits.length > 0 && (
            <div style={{ position: "absolute", zIndex: 30, background: "#fff", border: "1px solid var(--border)", borderRadius: 6, width: "100%", boxShadow: "0 6px 18px rgba(0,0,0,0.14)", maxHeight: 220, overflowY: "auto" }}>
              {claimHits.map(c => (
                <div key={c.id} onClick={() => { up("claim_id", c.id); setClaimLabel(c.claim_number); setClaimHits([]); setClaimQ(""); }}
                  style={{ padding: "6px 10px", cursor: "pointer", borderBottom: "1px solid var(--border-light,#f0f0f0)" }}>
                  <span className="mono" style={{ color: "var(--blue)", fontWeight: 600 }}>{c.claim_number}</span>
                  <span style={{ color: "var(--text2)", marginLeft: 8 }}>{c.insured_name || c.claimant_name || ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <label style={lbl}>Assigned Adjuster (review owner)</label>
      <select value={form.assigned_adjuster_id} onChange={e => up("assigned_adjuster_id", e.target.value)} style={inp} disabled={!canManage}>
        <option value="">— select —</option>
        {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
      </select>

      <div style={SECH}>Bill Basics</div>
      {/* core fields */}
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 2, position: "relative" }}>
          <label style={lbl}>Payee{!form.payee && <span style={{ color: "#dc2626", marginLeft: 4 }}>•</span>}</label>
          <input value={form.payee} placeholder="Search payees / vendors…" onChange={e => { up("payee", e.target.value); setPayeeQ(e.target.value); up("payee_company_id", ""); up("payee_contact_id", ""); up("payee_claimant_id", ""); setPayeeRdy(null); }} style={{ ...inp, ...(form.payee ? {} : { borderColor: "#dc2626" }) }} disabled={!canManage} />
          {canManage && form.claim_id && !form.payee_claimant_id && <div style={{ marginTop: 3, fontSize: 11 }}><button type="button" className="btn btn-sm btn-outline" style={{ padding: "1px 8px", fontSize: 11 }} disabled={busy} onClick={async () => { try { const r = await apiFetch(`/claims/${form.claim_id}/claimant-payee-ref`, { method: "POST" }); up("payee", r.name); up("payee_claimant_id", r.entity_id); up("payee_company_id", ""); up("payee_contact_id", ""); setPayeeRdy(r.readiness); } catch (e) { setMsg(String(e.message || e).slice(0, 150)); } }}>Pay the claimant</button></div>}
          {payeeHits.length > 0 && (
            <div style={{ position: "absolute", zIndex: 30, background: "#fff", border: "1px solid var(--border)", borderRadius: 6, width: "100%", boxShadow: "0 6px 18px rgba(0,0,0,0.14)", maxHeight: 240, overflowY: "auto" }}>
              {payeeHits.map(pp => (
                <div key={pp.kind + pp.entity_id} onClick={() => { up("payee", pp.name); up("payee_company_id", pp.kind === "company" ? pp.entity_id : ""); up("payee_contact_id", pp.kind === "contact" ? pp.entity_id : ""); up("payee_claimant_id", ""); setPayeeRdy(pp.readiness); setPayeeHits([]); setPayeeQ(""); }}
                  style={{ padding: "6px 10px", cursor: "pointer", borderBottom: "1px solid var(--border-light,#f0f0f0)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 600 }}>{pp.name}<span style={{ color: "var(--text3)", fontWeight: 400, marginLeft: 6, fontSize: 11 }}>{pp.kind}{pp.subtype ? " · " + pp.subtype : ""}</span></span>
                    <PayeeChips rd={pp.readiness} />
                  </div>
                </div>
              ))}
            </div>
          )}
          {(form.payee_company_id || form.payee_contact_id || form.payee_claimant_id) && <div style={{ marginTop: 4 }}>
            {payeeRdy && <div style={{ marginBottom: 4 }}><PayeeChips rd={payeeRdy} /></div>}
            {canManage && <button type="button" className="btn btn-sm btn-outline" style={{ padding: "1px 8px", fontSize: 11 }} onClick={async () => { try { await save({ silent: true }); } catch (e) { /* keep going — open the editor regardless */ } setPayeeModal({ kind: form.payee_company_id ? "company" : form.payee_contact_id ? "contact" : "claimant", entityId: form.payee_company_id || form.payee_contact_id || form.payee_claimant_id, name: form.payee }); }}>Edit payee / W-9 ✎</button>}
          </div>}
          {canManage && form.payee && !form.payee_company_id && !form.payee_contact_id && (
            <div style={{ marginTop: 4, fontSize: 11 }}>
              <span style={{ color: "var(--text3)" }}>No match. </span>
              <button type="button" className="btn btn-sm btn-outline" style={{ padding: "1px 8px", fontSize: 11 }} disabled={busy} onClick={quickCreatePayee}>+ Create "{form.payee}" as new payee</button>
              <span style={{ color: "var(--text3)", marginLeft: 6 }}>(routes to Accounting for vetting; won't block the bill)</span>
            </div>
          )}
        </div>
        <div style={{ flex: 1 }}><label style={lbl}>Bill No.</label><input value={form.invoice_number} onChange={e => up("invoice_number", e.target.value)} placeholder="invoice #" style={inp} disabled={!canManage} /></div>
        <div style={{ flex: 1.5, display: "flex", gap: 6 }}>
          <div style={{ flex: 1 }}><label style={lbl} title="Full invoice amount (reference)">Total bill</label><input type="number" step="0.01" value={form.total_bill_amount} placeholder="full" onChange={e => { const v = e.target.value; setForm(f => ({ ...f, total_bill_amount: v, amount: (f.amount === "" || f.amount == null) ? v : f.amount })); }} style={inp} disabled={!canManage} /></div>
          <div style={{ flex: 1 }}><label style={lbl} title="The portion we're responsible for — drives payment, QBO, and reports">Responsible{(form.amount === "" || parseFloat(form.amount) <= 0) && <span style={{ color: "#dc2626", marginLeft: 4 }}>•</span>}</label><input type="number" step="0.01" value={form.amount} onChange={e => up("amount", e.target.value)} style={{ ...inp, ...((form.amount === "" || parseFloat(form.amount) <= 0) ? { borderColor: "#dc2626" } : {}) }} disabled={!canManage} /></div>
        </div>
      </div>
      {form.total_bill_amount !== "" && form.amount !== "" && Math.abs((parseFloat(form.total_bill_amount) || 0) - (parseFloat(form.amount) || 0)) > 0.005 && (
        <div style={{ fontSize: 11, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 5, padding: "3px 8px", marginTop: 4 }}>
          Partial responsibility: total bill {money(parseFloat(form.total_bill_amount) || 0)}, we pay / report {money(parseFloat(form.amount) || 0)} (drives payment, QBO &amp; reports).
        </div>
      )}
      <div style={{ marginTop: 6 }}>
        <label style={lbl}>Description <span style={{ color: "var(--text3)", fontWeight: 400 }}>({(form.description || "").length}/42)</span></label>
        <input value={form.description} maxLength={42} onChange={e => up("description", e.target.value)} placeholder="short description (prints to QBO)" style={inp} disabled={!canManage} />
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
        <div style={{ flex: 1 }}><label style={lbl}>Issue date</label><input type="date" value={form.invoice_date} onChange={e => up("invoice_date", e.target.value)} style={inp} disabled={!canManage} /></div>
        <div style={{ flex: 1 }}><label style={lbl}>Due date</label><input type="date" value={form.due_date} onChange={e => up("due_date", e.target.value)} style={inp} disabled={!canManage} /></div>
      </div>
      <div style={SECH}>Coding</div>
      <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", margin: "2px 0 0" }}>Date(s) of service</div>
      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
        <div style={{ flex: 1 }}><label style={lbl}>Type</label>
          <select value={form.service_date_type || "single"} onChange={e => up("service_date_type", e.target.value)} style={inp} disabled={!canManage}>
            <option value="single">Single date</option>
            <option value="range">Date range</option>
            <option value="multiple">Multiple dates</option>
          </select>
        </div>
        <div style={{ flex: 1 }}><label style={lbl}>{form.service_date_type === "range" ? "Start date" : "Date of service"}</label><input type="date" value={form.date_of_service} onChange={e => up("date_of_service", e.target.value)} style={inp} disabled={!canManage} /></div>
      </div>
      {form.service_date_type === "range" && (
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <div style={{ flex: 1 }} />
          <div style={{ flex: 1 }}><label style={lbl}>End date</label><input type="date" value={form.service_date_end} onChange={e => up("service_date_end", e.target.value)} style={inp} disabled={!canManage} /></div>
        </div>
      )}
      {form.service_date_type === "multiple" && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 10, color: "var(--text3)", marginBottom: 3 }}>Specific dates of service (the date above is the primary/representative date)</div>
          {(form.service_dates || []).map((d, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 4, alignItems: "center" }}>
              <input type="date" value={d} disabled={!canManage}
                onChange={e => up("service_dates", form.service_dates.map((x, j) => (j === i ? e.target.value : x)))} style={inp} />
              {canManage && <button type="button" className="btn btn-sm btn-outline" style={{ padding: "0 7px" }} onClick={() => up("service_dates", form.service_dates.filter((_, j) => j !== i))}>×</button>}
            </div>
          ))}
          {canManage && <button type="button" className="btn btn-sm btn-outline" style={{ padding: "1px 8px", fontSize: 11 }} onClick={() => up("service_dates", [...(form.service_dates || []), ""])}>+ Add date</button>}
        </div>
      )}
      {/* WHAT IS IT (cont.) — Category. Single dropdown by default (≈ always); split is opt-in. */}
      <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontWeight: 600, color: "var(--text2)" }}>Category{!coded && <span style={{ color: "#dc2626", marginLeft: 4 }}>•</span>}</span>
        <span className="badge" style={{ fontSize: 9, background: "#eef2ff", color: "#3730a3" }}>{bill.business_line || "P&I"}</span>
        {canManage && (multiCat
          ? <button className="btn btn-sm btn-outline" style={{ marginLeft: "auto", padding: "1px 8px", fontSize: 11 }} onClick={() => { const first = allocs.find(a => a.category_id) || allocs[0] || {}; setAllocs(first.category_id ? [{ category_id: first.category_id, amount: amt }] : []); setMultiCat(false); }}>Single category</button>
          : <button className="btn btn-sm btn-outline" style={{ marginLeft: "auto", padding: "1px 8px", fontSize: 11 }} onClick={() => { setMultiCat(true); setAllocs([...(allocs[0] && allocs[0].category_id ? [allocs[0]] : []), { category_id: "", amount: "" }]); }}>Allocate to multiple…</button>)}
      </div>
      {!multiCat ? (
        <select value={(allocs[0] && allocs[0].category_id) || ""} onChange={e => setAllocs(e.target.value ? [{ category_id: e.target.value, amount: amt }] : [])} disabled={!canManage}
          style={{ ...inp, marginTop: 4, ...((allocs[0] && allocs[0].category_id) ? {} : { borderColor: "#dc2626" }) }}>
          <option value="">— select category (required) —</option>
          {catOptions}
        </select>
      ) : (
        <>
          {allocs.map((a, i) => {
            const cat = catById[a.category_id];
            return (
              <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
                <select value={a.category_id} onChange={e => upAlloc(i, "category_id", e.target.value)} disabled={!canManage} style={{ ...inp, padding: "3px 5px", fontSize: 11, flex: 2 }}>
                  <option value="">— category —</option>
                  {catOptions}
                </select>
                <input type="number" step="0.01" value={a.amount} onChange={e => upAlloc(i, "amount", e.target.value)} disabled={!canManage}
                  title={cat && cat.allows_negative ? "negative allowed (offset/recovery)" : ""} style={{ ...inp, padding: "3px 5px", fontSize: 11, flex: 1 }} />
                {canManage && <button className="btn btn-sm btn-outline" style={{ padding: "1px 7px", fontSize: 11 }} onClick={() => rmAlloc(i)}>×</button>}
              </div>
            );
          })}
          {canManage && <button className="btn btn-sm btn-outline" style={{ marginTop: 4, padding: "1px 8px", fontSize: 11 }} onClick={addAlloc}>+ Line</button>}
          <div style={{ marginTop: 6, fontSize: 11, color: mismatch ? "#dc2626" : "var(--text3)" }}>Allocated {money(allocSum)} {mismatch ? `≠ amount ${money(amt)}` : "= amount ✓"}</div>
        </>
      )}

      {/* WHAT WE'RE DOING WITH IT — funding / payor */}
      <div style={SECH}>Funding</div>
      {trustBal && (
        <div style={{ fontSize: 11, background: "var(--bg2,#f1f5f9)", borderRadius: 6, padding: "5px 8px", marginBottom: 6, display: "flex", gap: 14, flexWrap: "wrap" }}>
          <span>Claim trust balance: <b style={{ color: trustBal.claim_balance < 0 ? "#991b1b" : "#166534" }}>{fmt.usd ? fmt.usd(trustBal.claim_balance) : `$${trustBal.claim_balance}`}</b></span>
          {trustBal.general_trust && <span>{trustBal.general_trust.company_name} general trust ({trustBal.general_trust.short_code || trustBal.general_trust.account_name}): <b style={{ color: trustBal.general_trust.balance < 0 ? "#991b1b" : "#166534" }}>{fmt.usd ? fmt.usd(trustBal.general_trust.balance) : `$${trustBal.general_trust.balance}`}</b></span>}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
        <div style={{ flex: 1 }}><label style={lbl}>Payor</label>
          <select value={form.payor_type} onChange={e => up("payor_type", e.target.value)} style={inp} disabled={!canManage}>
            <option value="">—</option>
            <option value="trust">Trust</option>
            <option value="insured">Insured</option>
            <option value="underwriter">Underwriter</option>
          </select>
        </div>
        {form.payor_type === "trust"
          ? <div style={{ flex: 1 }}><label style={lbl}>Funding method</label>
              <select value={form.payment_mechanism_id} onChange={e => up("payment_mechanism_id", e.target.value)} style={inp} disabled={!canManage}>
                <option value="">—</option>
                {mechanisms.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </div>
          : <div style={{ flex: 1 }} />}
      </div>
      {form.payor_type === "trust" && (
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>Accounting confirms the trust account &amp; checkstock when the bill reaches the Bill Queue.</div>
      )}
      {(form.payor_type === "insured" || form.payor_type === "underwriter") && (
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>Paid directly by the {form.payor_type}. On approval this becomes a <b>Payment Request</b> in Funds Requests for tracking.</div>
      )}
      {/* Accounting implementation details — hidden from adjusters; collapsed for Ops/Accounting. */}
      {can("manage_bill_intake") && (
        <Collapsible title="Accounting details">
          <div style={{ fontSize: 11, color: "var(--text3)" }}>
            QBO memo: <span className="mono" style={{ color: "var(--text2)" }}>{bill.memo || "—"}</span>
          </div>
          {bill.bill_no_code && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 3 }}>Bill No.: <span className="mono" style={{ color: "var(--text2)" }}>{bill.bill_no_code}</span></div>}
        </Collapsible>
      )}
      {msg && <div style={{ marginTop: 8, fontSize: 12, color: /saved|routed/i.test(msg) ? "#16a34a" : "#dc2626" }}>{msg}</div>}
      {canManage && (
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
          <button className="btn btn-sm btn-outline" disabled={busy} onClick={() => save()}>Save Coding</button>
          <span style={{ fontSize: 11, color: "var(--text3)" }}>Route · approve · audit · kick back live in the Workflow panel above.</span>
          <div style={{ flex: 1 }} />
          {/* WS0-B H4: Delete is offered only where the server allows it. Once a bill carries a
              payments row, or has reached Approved, DELETE returns 409 and the reversal is
              Void — deleting a Payment Ready bill is what left 9 orphan payments in
              production, each still counted by v_claim_financials.total_paid. */}
          {can("manage_bill_intake") && (bill.payment_id || NO_DELETE_STATUSES.includes(bill.current_status)
            ? <span style={{ fontSize: 11, color: "var(--text3)" }}>
                {bill.payment_id
                  ? "A payment is recorded against this bill — it can't be deleted; Void it instead."
                  : `${dispStatus(bill.current_status)} bills are Voided, not deleted, so the approval stays auditable.`}
              </span>
            : <button className="btn btn-sm btn-outline" disabled={busy}
                onClick={async () => {
                  if (!window.confirm("Delete this bill entirely? It's removed from all queues. This can't be undone from the UI.")) return;
                  setBusy(true);
                  try { await apiFetch(`/bills/${bill.id}`, { method: "DELETE" }); onSaved && onSaved(); }
                  catch (e) { setMsg(String(e.message || e)); } finally { setBusy(false); }
                }}
                style={{ color: "#fff", background: "#991b1b", borderColor: "#991b1b" }}>Delete Bill</button>)}
        </div>
      )}
      {payeeModal && <PayeeProfileModal kind={payeeModal.kind} entityId={payeeModal.entityId} name={payeeModal.name} onClose={() => { setPayeeModal(null); if (bill.payee_company_id) apiFetch(`/payees/entity/company/${bill.payee_company_id}`).then(d => setPayeeRdy(d.readiness)).catch(() => {}); else if (bill.payee_contact_id) apiFetch(`/payees/entity/contact/${bill.payee_contact_id}`).then(d => setPayeeRdy(d.readiness)).catch(() => {}); onSaved && onSaved(); }} />}
    </div>
  );
}

// History timeline (compact) — rows only; the Collapsible wrapper supplies the title.
function BillHistory({ history }) {
  if (!history || !history.length) return null;
  return (
    <div>
      {history.map((h, i) => (
        <div key={i} style={{ fontSize: 11, color: "var(--text3)", padding: "2px 0", display: "flex", gap: 6 }}>
          <span style={{ color: STATUS_COLOR[h.to_status] || "var(--text2)", fontWeight: 600 }}>{dispStatus(h.to_status)}</span>
          <span>· {h.changed_by_name || "system"} · {fmt.datetime(h.created_at)}</span>
          {h.reason && <span style={{ fontStyle: "italic" }}>— {h.reason}</span>}
        </div>
      ))}
    </div>
  );
}

// Detail (split): document left, metadata + stepper + actions/coding right.
// ─── Scope H: medical-audit section on a bill (LD-BILLUX-007) ────────────────
// Send-to-Audit + lifecycle (sent → returned[EOB+reduction] → close; reconsider
// branch). H2 savings (billed → reduction → final). H3 audit fee as a linked bill
// created when the invoice arrives. Server enforces caps (non-default vendor →
// APPROVE_AUDIT_VENDOR); we mirror the gate client-side via can_approve_nondefault.
function AuditSection({ bill, onChanged }) {
  const [data, setData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [finalAmt, setFinalAmt] = useState("");
  const [feeAmt, setFeeAmt] = useState("");
  const [reconNote, setReconNote] = useState("");
  const load = useCallback(() => { apiFetch(`/bills/${bill.id}/audit`).then(setData).catch(() => setData(null)); }, [bill.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { apiFetch("/audit-vendors").then(d => setVendors(d || [])).catch(() => {}); }, []);
  const refresh = () => { setFinalAmt(""); setFeeAmt(""); setReconNote(""); load(); onChanged && onChanged(); };
  const act = (fn) => async () => {
    if (busy) return; setBusy(true); setMsg(null);
    try { await fn(); refresh(); } catch (e) { setMsg(String(e.message || e).slice(0, 200)); } finally { setBusy(false); }
  };
  if (!data) return null;
  const a = data.audit;
  if (!a || a.status === "closed" || a.status === "cancelled") {
    const chosen = vendors.find(v => String(v.id) === String(vendorId));
    const blocked = chosen && !chosen.is_default && !data.can_approve_nondefault;
    return (
      <Collapsible title="Medical Audit" tone="#7c3aed" defaultOpen={!!a}>
        {a && <div style={{ fontSize: 11.5, color: "var(--text3)", marginBottom: 6 }}>Previous audit {a.status}. You can send again.</div>}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={vendorId} onChange={e => setVendorId(e.target.value)} style={{ ...inp, width: "auto", flex: 1, minWidth: 160 }} disabled={busy}>
            <option value="">Default vendor…</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}{v.is_default ? " (default)" : ""}</option>)}
          </select>
          <button className="btn btn-sm btn-blue" disabled={busy || blocked}
            onClick={act(() => apiFetch(`/bills/${bill.id}/send-to-audit`, { method: "POST", body: vendorId ? { vendor_id: parseInt(vendorId) } : {} }))}>Send to Audit</button>
        </div>
        {blocked && <div style={{ fontSize: 11, color: "#92400e", marginTop: 6 }}>⚠ {chosen.name} is non-default — sending requires manager approval.</div>}
        {msg && <div style={{ marginTop: 6, fontSize: 11.5, color: "#991b1b" }}>{msg}</div>}
      </Collapsible>
    );
  }

  const canRecord = a.status === "sent" || a.status === "reconsideration";
  return (
    <Collapsible title="Medical Audit" tone="#7c3aed" badge={`${a.vendor_name || "vendor"} · ${a.status}`} defaultOpen={true}>
      <div style={{ fontSize: 11.5, color: "var(--text2)", marginBottom: 8 }}>
        Sent {(a.sent_at || "").slice(0, 10)}{a.returned_at ? ` · Returned ${a.returned_at.slice(0, 10)}` : ""}
      </div>
      {/* WS0-B H22: `original_amount` is the RESPONSIBLE amount that went to audit, not the
          full billed total — labelling it "Billed" was wrong on any TPA bill, where
          total_bill_amount ≠ amount. `original_total_bill_amount` (mig 145) carries the true
          total; it is shown separately, and only when the two actually differ. */}
      {a.final_amount != null ? (
        <div style={{ display: "flex", gap: 18, marginBottom: 8, fontSize: 12, flexWrap: "wrap" }}>
          {a.original_total_bill_amount != null && Number(a.original_total_bill_amount) !== Number(a.original_amount) &&
            <div>Total billed<br /><b style={{ color: "var(--text2)" }}>{money(a.original_total_bill_amount)}</b></div>}
          <div>Sent to audit<br /><b style={{ textDecoration: "line-through", color: "var(--text3)" }}>{money(a.original_amount)}</b></div>
          <div>Reduction<br /><b style={{ color: "#b45309" }}>−{money(a.reduction_amount)}</b></div>
          <div>Final (payable)<br /><b style={{ color: "#166534" }}>{money(a.final_amount)}</b></div>
        </div>
      ) : (
        <div style={{ fontSize: 12, marginBottom: 8 }}>
          Sent to audit <b>{money(a.original_amount)}</b>
          {a.original_total_bill_amount != null && Number(a.original_total_bill_amount) !== Number(a.original_amount) &&
            <> (of {money(a.original_total_bill_amount)} billed)</>} — awaiting EOB / reduction.
        </div>
      )}
      {canRecord && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
          <input value={finalAmt} onChange={e => setFinalAmt(e.target.value)} placeholder="Final (reduced) amount" style={{ ...inp, width: 170 }} disabled={busy} />
          <button className="btn btn-sm btn-blue" disabled={busy || finalAmt === ""}
            onClick={act(() => apiFetch(`/bill-audits/${a.id}/record-return`, { method: "POST", body: { final_amount: parseFloat(finalAmt) } }))}>Record EOB / reduction</button>
        </div>
      )}
      {a.status === "returned" && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
          <input value={reconNote} onChange={e => setReconNote(e.target.value)} placeholder="Reconsideration note…" style={{ ...inp, flex: 1, minWidth: 140 }} disabled={busy} />
          <button className="btn btn-sm btn-outline" disabled={busy || !reconNote.trim()} onClick={act(() => apiFetch(`/bill-audits/${a.id}/reconsider`, { method: "POST", body: { notes: reconNote.trim() } }))}>Reconsider</button>
          <button className="btn btn-sm btn-outline" disabled={busy} onClick={act(() => apiFetch(`/bill-audits/${a.id}/close`, { method: "POST", body: {} }))}>Close audit</button>
        </div>
      )}
      {a.reconsideration_notes && <div style={{ fontSize: 11, color: "var(--text2)", whiteSpace: "pre-wrap", marginBottom: 8 }}>Recon: {a.reconsideration_notes}</div>}
      <div style={{ borderTop: "1px dashed var(--border)", paddingTop: 8, marginTop: 4 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, color: "var(--text3)", marginBottom: 4 }}>AUDIT FEE</div>
        {data.fee_bill ? (
          <div style={{ fontSize: 12 }}>Linked fee bill <span className="mono">#{data.fee_bill.id}</span> · {money(data.fee_bill.amount)} · {data.fee_bill.current_status}</div>
        ) : (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 11.5, color: "var(--text3)" }}>Awaiting fee invoice.</span>
            <input value={feeAmt} onChange={e => setFeeAmt(e.target.value)} placeholder="Fee amount" style={{ ...inp, width: 110 }} disabled={busy} />
            <button className="btn btn-sm btn-outline" disabled={busy || feeAmt === ""} onClick={act(() => apiFetch(`/bills/${bill.id}/audit-fee`, { method: "POST", body: { amount: parseFloat(feeAmt) } }))}>Enter fee invoice</button>
          </div>
        )}
      </div>
      {msg && <div style={{ marginTop: 6, fontSize: 11.5, color: "#991b1b" }}>{msg}</div>}
    </Collapsible>
  );
}

// The single forward "happy-path" target per status → the primary Next Action.
const PRIMARY_TARGET = {
  "Received": "Routed", "Needs Claim": "Routed", "Needs Coding": "Routed",
  "Routed": "Adjuster Review", "Adjuster Review": "Approved", "Audit": "Approved",
  "Approved": "Accounting Review", "Accounting Review": "Payment Ready",
  "Payment Ready": "Queued in QBO", "Queued in QBO": "Paid",
  "Hold": "Adjuster Review", "Request Info": "Adjuster Review",
};
// Readiness checklist rows from the server-computed bill.readiness block.
// W-9 / vetting are WARN (override path exists), the rest are hard gates.
// The adjuster's approval blockers (Kuhrt 2026-06-27): payee, amount, a category,
// a payor, and — for trust bills — a funding method. W-9 / vetting are Accounting's,
// shown only as soft context here.
function readinessRows(rd) {
  const rows = [
    { label: "Payee selected", state: rd.payee_linked ? "ok" : "bad" },
    { label: "Amount entered", state: rd.amount_set ? "ok" : "bad" },
    { label: "Category set", state: rd.coding_complete ? "ok" : "bad" },
    { label: "Payor selected", state: rd.funding_selected ? "ok" : "bad" },
  ];
  if (rd.is_trust) rows.push({ label: "Funding method", state: rd.funding_method_ok ? "ok" : "bad" });
  rows.push({ label: rd.w9_override ? "W-9 overridden" : rd.w9_ok ? "W-9 on file" : "W-9 (Accounting)",
    state: rd.w9_ok ? "ok" : "warn" });
  if (rd.payment_hold) rows.push({ label: "Payment hold", state: "bad" });
  return rows;
}

// One cohesive Workflow Command Center — where the bill is, what's next, why it
// can/can't move. Replaces the old status-chip + stepper + next-step + action-bar
// stack. Forward actions persist pending coding edits first (via codingRef) so a
// click up top never silently drops edits below; the server is the gate and its
// message surfaces here. Shared by every surface (Ops intake = claim Bills tab).
const SECLBL = { fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--text3)" };
function WorkflowCommandCenter({ bill, surface, claim, onChanged, codingRef }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const user = getUser();
  const status = bill.current_status;
  const rd = bill.readiness || {};

  const targets = (TX[status] || []).filter(to => {
    const m = ACT[to] || {};
    // Ops (admin) owns intake + routing ONLY. Accounting actions (Send to Accounting,
    // Payment Ready, Queue in QBO, Mark Paid) live in the Accounting Bill Queue — never
    // on the Ops surface. Once a bill is routed/approved, Ops has no further action here —
    // EXCEPT returning an info request (Request Info / Hold) back to the adjuster.
    if (surface === "admin") {
      if (["Request Info", "Hold"].includes(status)) return to === "Adjuster Review";
      return ["Received", "Needs Claim", "Needs Coding"].includes(status) && ["Needs Coding", "Routed"].includes(to);
    }
    if (!m.claim) return false;
    if (claim && user) {
      const capMap = { "Adjuster Review": "review_bills", "Hold": "review_bills", "Request Info": "review_bills",
        "Rejected": "review_bills", "Approved": "approve_bills", "Payment Ready": "approve_bills", "Audit": "send_bill_to_audit" };
      return canOnClaim(capMap[to], claim, user);
    }
    return true;
  });

  const doTransition = async (to, reason) => {
    setBusy(true); setErr(null);
    try {
      if (codingRef?.current?.save) { try { await codingRef.current.save({ silent: true }); } catch (e) { /* surfaced below if the move fails */ } }
      await apiFetch(`/bills/${bill.id}/transition`, { method: "POST", body: { to_status: to, reason } });
      // Insured/Underwriter-paid bill → on approval, offer to open a Payment Request.
      if (to === "Approved" && (bill.payor_type === "insured" || bill.payor_type === "underwriter") && bill.claim_id) {
        const payer = bill.payor_type;
        let exists = false;
        try { const d = await apiFetch(`/claims/${bill.claim_id}/payment-requests`); exists = (d.payment_requests || []).some(p => p.bill_id === bill.id); } catch (e) {}
        if (exists) window.alert("Bill approved. A Payment Request for this bill already exists under the claim's Funds Requests tab.");
        else if (window.confirm(`This bill is paid directly by the ${payer}.\n\nA Payment Request will be created in the claim's Funds Requests to track it — and converted to a Payment once you mark it Confirmed Paid. Create it now?`)) {
          try {
            await apiFetch(`/claims/${bill.claim_id}/payment-requests`, { method: "POST", body: {
              bill_id: bill.id, requested_payer: payer, payee: bill.payee,
              payee_company_id: bill.payee_company_id || null, payee_contact_id: bill.payee_contact_id || null,
              amount: bill.amount, date_of_service: bill.date_of_service || null,
              service_date_type: bill.service_date_type || "single", service_date_end: bill.service_date_end || null } });
            window.alert("Payment Request created — see the claim's Funds Requests tab to send it and confirm payment.");
          } catch (e) { window.alert("Bill approved, but the Payment Request couldn't be created: " + String(e.message || e).slice(0, 160)); }
        }
      }
      onChanged && onChanged();
    } catch (e) { setErr(String(e.message || e).replace(/^"|"$/g, "").slice(0, 220)); } finally { setBusy(false); }
  };

  // Primary forward action. On the admin surface the route is a save+validate+route
  // chain (reuse CodingPanel's logic via codingRef); elsewhere it's a single transition.
  const isAdminRoute = surface === "admin" && ["Received", "Needs Claim", "Needs Coding"].includes(status);
  let primary = null;
  let secondaryTos = targets;
  if (isAdminRoute) {
    primary = { label: "Route to Adjuster/Claim →", color: "#2563eb",
      run: async () => { setErr(null); setBusy(true); try { await codingRef?.current?.routeToAdjuster?.(); } catch (e) { setErr(String(e.message || e).slice(0, 220)); } finally { setBusy(false); } } };
    secondaryTos = targets.filter(to => to !== "Needs Coding" && to !== "Routed");
  } else {
    const pt = PRIMARY_TARGET[status];
    const primaryTo = targets.includes(pt) ? pt : (targets[0] || null);
    if (primaryTo) {
      const meta = ACT[primaryTo] || {};
      primary = { label: actLabel(status, primaryTo), color: meta.color || "#2563eb", to: primaryTo,
        run: () => doTransition(primaryTo, null) };
      // Ops answering an info request → "Return to adjuster" with an optional note.
      if (surface === "admin" && primaryTo === "Adjuster Review" && ["Request Info", "Hold"].includes(status)) {
        primary.label = "Return to adjuster →";
        primary.run = () => { const note = window.prompt("Note for the adjuster (optional — e.g. “Answered — see the attached doc”):"); if (note === null) return; doTransition("Adjuster Review", note.trim() || null); };
      }
      secondaryTos = targets.filter(to => to !== primaryTo);
    }
  }
  // Context (non-pipeline) actions — kick back to Ops / disclaim.
  const ctx = [];
  if ((surface === "review" || surface === "claim") && bill.claim_id && ["Routed", "Adjuster Review", "Hold", "Request Info"].includes(status))
    ctx.push({ label: "Kick back to Ops", run: () => codingRef?.current?.kickback?.() });
  if (surface === "review" && !bill.claim_id)
    ctx.push({ label: "Not one of my claims", run: () => codingRef?.current?.disclaim?.() });

  const next = NEXT_STEP[status];
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 10, padding: "10px 12px", background: "#fff", marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 7 }}>
        <BillStatusChip status={status} />
        {bill.claim_number && <span className="mono" style={{ color: "var(--blue)", fontWeight: 700, fontSize: 12.5 }}>{bill.claim_number}</span>}
        <span style={{ marginLeft: "auto", fontWeight: 800, color: "var(--navy)" }}>{money(bill.amount)}</span>
      </div>
      <div style={SECLBL}>Workflow</div>
      <div style={{ margin: "5px 0 9px" }}><BillStepper status={status} /></div>
      <div style={SECLBL}>Next action</div>
      {next && <div style={{ fontSize: 12, color: "var(--text2)", margin: "2px 0 6px" }}>{next.t}</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {primary
          ? <button className="btn" disabled={busy} onClick={primary.run}
              style={{ background: primary.color, color: "#fff", borderColor: primary.color, fontWeight: 700, padding: "7px 16px" }}>{primary.label}</button>
          : <span style={{ fontSize: 12, color: "var(--text3)" }}>No action available in this view.</span>}
        {secondaryTos.map(to => <ActionButton key={to} from={status} to={to} onGo={doTransition} busy={busy} />)}
        {ctx.map((c, i) => <button key={`c${i}`} className="btn btn-sm btn-outline" disabled={busy} onClick={c.run} style={{ color: "#92400e", borderColor: "#fde68a" }}>{c.label}</button>)}
      </div>
      {err && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 7 }}>{err}</div>}
      {bill.readiness && (
        <>
          <div style={{ ...SECLBL, marginTop: 10 }}>Ready to proceed</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 5 }}>
            {readinessRows(rd).map((r, i) => {
              const c = r.state === "ok" ? { fg: "#166534", bg: "#dcfce7", ic: "✓" }
                : r.state === "warn" ? { fg: "#92400e", bg: "#fffbeb", ic: "⚠" }
                : { fg: "#991b1b", bg: "#fee2e2", ic: "✗" };
              return <span key={i} style={{ fontSize: 11, fontWeight: 600, color: c.fg, background: c.bg, borderRadius: 5, padding: "2px 8px" }}>{c.ic} {r.label}</span>;
            })}
          </div>
        </>
      )}
    </div>
  );
}

function BillDetail({ billId, surface, claim, onChanged }) {
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const advancedRef = useRef(null);
  const codingRef = useRef(null);   // CodingPanel publishes save/route/kickback/disclaim here
  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`/bills/${billId}`).then(b => { setBill(b); setLoading(false); }).catch(() => setLoading(false));
  }, [billId]);
  useEffect(() => { load(); }, [load]);
  const changed = useCallback(() => { load(); onChanged && onChanged(); }, [load, onChanged]);
  // Scope C: when the adjuster opens a freshly-routed bill, advance it into
  // "Adjuster Review" automatically (Routed = in the inbox; Adjuster Review =
  // opened). Server enforces the claim capability; admin surface never fires.
  useEffect(() => {
    if (!bill || advancedRef.current === bill.id) return;
    if (bill.current_status === "Routed" && (surface === "review" || surface === "claim")) {
      advancedRef.current = bill.id;
      apiFetch(`/bills/${bill.id}/transition`, { method: "POST", body: { to_status: "Adjuster Review" } })
        .then(() => changed()).catch(() => {});
    }
  }, [bill, surface, changed]);
  if (loading) return <div style={{ padding: 24, color: "var(--text3)" }}>Loading bill…</div>;
  if (!bill) return <div style={{ padding: 24, color: "var(--text3)" }}>Bill not found.</div>;
  const showCoding = (surface === "admin" || surface === "claim" || surface === "review");
  return (
    <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
      {/* Doc column narrowed (Kuhrt 2026-06-27) so the work column (coding) is the
          wider one — more visible + doable above the fold on a laptop. */}
      <div style={{ flex: "1 1 360px", minWidth: 300 }}>
        <BillDocPreview bill={bill} height={620} />
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 6 }}>
          {bill.original_filename ? <>{bill.original_filename} · {bill.bytes_stored ? `${Math.round(bill.bytes_stored / 1024)} KB` : ""}</> : "No file"}
        </div>
      </div>
      <div style={{ flex: "1.35 1 440px", minWidth: 380 }}>
        {/* ONE Workflow Command Center: where it is · what's next · why blocked */}
        <WorkflowCommandCenter bill={bill} surface={surface} claim={claim} onChanged={changed} codingRef={codingRef} />
        {/* grouped coding form (Basics / Coding / Funding) */}
        {showCoding && <div style={{ marginTop: 4 }}><CodingPanel bill={bill} surface={surface} onSaved={changed} codingRef={codingRef} /></div>}
        {showCoding && bill.source !== "audit_fee" && <AuditSection bill={bill} onChanged={changed} />}
        {bill.history && bill.history.length > 0 &&
          <Collapsible title="History" badge={bill.history.length}><BillHistory history={bill.history} /></Collapsible>}
      </div>
    </div>
  );
}
const MetaRow = ({ label, value }) => value ? (
  <tr><td style={{ color: "var(--text3)", padding: "2px 8px 2px 0", whiteSpace: "nowrap", verticalAlign: "top" }}>{label}</td>
    <td style={{ color: "var(--text1,#1a2438)", padding: "2px 0" }}>{value}</td></tr>
) : null;

// Compact work-state pill for the queue cards (payor / W-9 / new payee).
const QPill = ({ text, fg, bg, title }) => (
  <span title={title} style={{ fontSize: 9.5, fontWeight: 700, color: fg, background: bg, borderRadius: 4, padding: "1px 6px", whiteSpace: "nowrap" }}>{text}</span>
);

// Generic queue list row. `hideClaim` drops the redundant claim chip on the
// claim-level panel (where every bill belongs to the open claim). The third row
// surfaces operational work-state (payor · W-9 missing · new payee) so the queue
// communicates work, not just identifies the bill.
function QueueRow({ b, active, onClick, showReviewer, hideClaim }) {
  // In the claim context the claim is implied; only Hold/Request-Info count as
  // exceptions there (a missing claim_number is not "untagged").
  const exc = hideClaim ? ["Hold", "Request Info"].includes(b.current_status) : isException(b);
  return (
    <div onClick={onClick} style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)", cursor: "pointer",
      background: active ? "#eff6ff" : "#fff", borderLeft: active ? "3px solid var(--blue)" : exc ? "3px solid #d9770699" : "3px solid transparent" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {exc && <span title="Needs attention (untagged / Hold / Request Info)" style={{ flexShrink: 0, color: "#d97706" }}>⚠</span>}
        <span style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 12.5, color: "var(--navy)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {b.payee || "(no payee)"} {b.has_document && <span title="has document" style={{ color: "var(--text3)" }}>📎</span>}
        </span>
        <span style={{ flexShrink: 0, fontWeight: 700, fontSize: 12 }}>{money(b.amount)}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3 }}>
        {!hideClaim && <span className="mono" style={{ fontSize: 10.5, color: b.claim_number ? "var(--blue)" : "#d97706" }}>{b.claim_number || "untagged"}</span>}
        <BillStatusChip status={b.current_status} small />
        {Number(b.audit_savings) > 0
          ? <span title="Medical-audit savings" style={{ fontSize: 10, fontWeight: 700, color: "#166534", background: "#dcfce7", borderRadius: 4, padding: "0 5px" }}>↓ {money(b.audit_savings)}</span>
          : (b.audit_status === "sent" || b.audit_status === "reconsideration")
            ? <span title="At medical audit" style={{ fontSize: 10, fontWeight: 700, color: "#92400e", background: "#fffbeb", borderRadius: 4, padding: "0 5px" }}>Waiting on Audit</span>
            : null}
        {showReviewer && b.assigned_adjuster_name && <span style={{ fontSize: 10, color: "var(--text3)" }}>{b.assigned_adjuster_name}</span>}
        <span style={{ marginLeft: "auto", fontSize: 10, color: "var(--text3)" }}>{b.received_date ? fmt.date(b.received_date) : ""}{b.age_days != null ? ` · ${b.age_days}d` : ""}</span>
      </div>
      {(b.payor_type || b.w9_missing || b.new_payee || b.ofac_missing || b.ofac_hit) && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4, flexWrap: "wrap" }}>
          {b.payor_type && <QPill text={{ trust: "Trust", underwriter: "Underwriter", insured: "Insured" }[b.payor_type] || b.payor_type}
            fg="#3730a3" bg="#eef2ff" title="Payor" />}
          {b.w9_missing && <QPill text="W-9 Missing" fg="#991b1b" bg="#fee2e2" title="Payee has no W-9 on file" />}
          {b.ofac_missing && <QPill text="OFAC Missing" fg="#92400e" bg="#fffbeb" title="No OFAC screening on file" />}
          {b.ofac_hit && <QPill text="OFAC Hit" fg="#991b1b" bg="#fee2e2" title="OFAC screening flagged a hit — review" />}
          {b.new_payee && <QPill text="New Payee" fg="#92400e" bg="#fffbeb" title="Payee awaiting vetting" />}
        </div>
      )}
    </div>
  );
}

// Sortable + sticky-toolbar queue list (Operations + adjuster review surfaces).
const QUEUE_SORTS = [
  { k: "received", label: "Received", fn: (a, b) => String(b.received_date || "").localeCompare(String(a.received_date || "")) },
  { k: "amount", label: "Amount", fn: (a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0) },
  { k: "payee", label: "Payee", fn: (a, b) => String(a.payee || "").localeCompare(String(b.payee || "")) },
  { k: "status", label: "Status", fn: (a, b) => String(a.current_status || "").localeCompare(String(b.current_status || "")) },
];
function QueueList({ bills, sel, onSelect, showReviewer, allowExceptionFilter, emptyText }) {
  const [sort, setSort] = useState("received");
  const [exOnly, setExOnly] = useState(false);
  const sf = QUEUE_SORTS.find(s => s.k === sort) || QUEUE_SORTS[0];
  const rows = [...(exOnly ? bills.filter(isException) : bills)].sort(sf.fn);
  const sbtn = (active) => ({ fontSize: 10.5, padding: "2px 7px", borderRadius: 10, border: "1px solid var(--border)", cursor: "pointer",
    background: active ? "var(--navy)" : "#fff", color: active ? "#fff" : "var(--text2)" });
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden", maxHeight: "72vh", overflowY: "auto" }}>
      <div style={{ position: "sticky", top: 0, zIndex: 5, background: "#f8fafc", borderBottom: "1px solid var(--border)", padding: "6px 8px", display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, color: "var(--text3)", fontWeight: 700, textTransform: "uppercase" }}>Sort</span>
        {QUEUE_SORTS.map(s => <button key={s.k} onClick={() => setSort(s.k)} style={sbtn(sort === s.k)}>{s.label}</button>)}
        {allowExceptionFilter && (
          <button onClick={() => setExOnly(v => !v)} title="Show only bills needing attention (untagged / Hold / Request Info)"
            style={{ marginLeft: "auto", fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 10, cursor: "pointer",
              border: `1px solid ${exOnly ? "#d97706" : "var(--border)"}`, background: exOnly ? "#fffbeb" : "#fff", color: exOnly ? "#92400e" : "var(--text2)" }}>⚠ Exceptions</button>
        )}
      </div>
      {rows.length === 0
        ? <div style={{ padding: 20, color: "var(--text3)", fontSize: 13, textAlign: "center" }}>{emptyText || "No bills."}</div>
        : rows.map(b => <QueueRow key={b.id} b={b} active={sel === b.id} showReviewer={showReviewer} onClick={() => onSelect(b.id)} />)}
    </div>
  );
}

// ─── SURFACE 1: Operations → Bill Intake queue (admin) ───────────────────────
export function BillIntakeQueue() {
  const [bills, setBills] = useState([]);
  const [activeChip, setActiveChip] = useState("All");
  const [reimbOnly, setReimbOnly] = useState(false);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const fileRef = useRef(null);
  const load = useCallback(() => {
    apiFetch("/bills").then(d => setBills(d || [])).catch(() => setBills([]));
  }, []);
  useEffect(() => { load(); }, [load, tick]);
  const refresh = () => setTick(t => t + 1);

  // Grouped, client-side filtering: each chip maps to a set of real statuses;
  // active queues exclude terminal (Paid/Rejected) — those live in Payment History.
  const filtered = useMemo(() => {
    const grp = CHIP_GROUPS.find(g => g.label === activeChip) || CHIP_GROUPS[0];
    return bills.filter(b => {
      if (ACTIVE_EXCLUDE.includes(b.current_status)) return false;
      if (grp.statuses && !grp.statuses.includes(b.current_status)) return false;
      if (reimbOnly && !(b.funding_disposition === "wnw_reimbursable" && !b.reimbursement_request_id)) return false;
      return true;
    });
  }, [bills, activeChip, reimbOnly]);

  const counts = useMemo(() => {
    const active = bills.filter(b => !ACTIVE_EXCLUDE.includes(b.current_status));
    const m = {};
    CHIP_GROUPS.forEach(g => { m[g.label] = g.statuses == null ? active.length : active.filter(b => g.statuses.includes(b.current_status)).length; });
    return m;
  }, [bills]);

  // Scope C: if the selected bill leaves the current view (after a workflow
  // action or a filter change), advance to the next bill — or drop to intake.
  useEffect(() => {
    if (sel != null && !filtered.some(b => b.id === sel)) setSel(filtered[0]?.id ?? null);
  }, [filtered, sel]);

  // Scope A: document-first. Drop/pick uploads immediately and makes the new
  // bill the active document for in-place coding — no blind modal.
  const uploadFile = async (file) => {
    if (!file || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", "admin_upload");
      const res = await apiFetch("/bills", { raw: true, method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      const b = await res.json();
      setActiveChip("All");
      setSel(b.id);
      refresh();
    } catch (e) { window.alert("Upload failed: " + String(e.message || e).slice(0, 200)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {CHIP_GROUPS.map(g => (
            <button key={g.label} onClick={() => setActiveChip(g.label)} style={{ ...chip, ...(activeChip === g.label ? chipActive : {}) }}>
              {g.label}{counts[g.label] ? <span style={{ marginLeft: 5, opacity: 0.65, fontWeight: 700 }}>{counts[g.label]}</span> : null}
            </button>
          ))}
          <button onClick={() => setReimbOnly(v => !v)} title="Show only WNW-reimbursable bills not yet on a funds request"
            style={{ ...chip, ...(reimbOnly ? { background: "#fffbeb", color: "#92400e", borderColor: "#d97706" } : {}) }}>⟲ Reimbursable</button>
        </div>
        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.webp,.gif" style={{ display: "none" }}
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadFile(f); }} />
        <button className="btn btn-blue btn-sm" style={{ marginLeft: "auto" }} disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? "Uploading…" : "+ Add Bill"}</button>
      </div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ width: 320, flexShrink: 0 }}>
          <QueueList bills={filtered} sel={sel} onSelect={setSel} showReviewer
            emptyText={`No bills${activeChip !== "All" ? ` in ${activeChip}` : ""}.`} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {sel ? <BillDetail billId={sel} surface="admin" onChanged={refresh} />
            : <IntakeDropZone busy={busy} prominent={filtered.length === 0} onPick={() => fileRef.current?.click()} onFile={uploadFile} />}
        </div>
      </div>
    </div>
  );
}

// Document-first empty state — LD-DESIGN-012 pattern 4: a modest centered drop
// card (never a full-panel billboard) that uploads on drop and opens the new bill
// in the viewer. `prominent` (queue empty) grants it a little more presence.
function IntakeDropZone({ busy, onPick, onFile, prominent = false, title = "Drop a bill to start", subtitle = "PDF or image — it opens right here for coding & routing." }) {
  const [over, setOver] = useState(false);
  return (
    <div onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
      style={{ padding: prominent ? "28px 24px" : "18px 20px", textAlign: "center", border: `2px dashed ${over ? "var(--blue)" : "var(--border)"}`,
        borderRadius: 10, background: over ? "#eff6ff" : "#fafbfe", transition: "all .12s",
        maxWidth: prominent ? 520 : 440, maxHeight: 200, margin: "0 auto", boxSizing: "border-box",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--navy)" }}>
        <span style={{ fontSize: prominent ? 22 : 17, verticalAlign: "middle", marginRight: 7, color: over ? "var(--blue)" : "var(--text3)" }}>⤓</span>
        {busy ? "Uploading…" : title}
      </div>
      <div style={{ fontSize: 11.5, color: "var(--text2)", marginTop: 4 }}>{subtitle}</div>
      <button className="btn btn-blue btn-sm" style={{ marginTop: 10 }} disabled={busy} onClick={onPick}>Choose a file</button>
      {!prominent && <div style={{ fontSize: 11, marginTop: 8, color: "var(--text3)" }}>…or pick an item from the queue on the left.</div>}
    </div>
  );
}

// ─── Arc 1 Wave 3: UNIFIED INBOX (Invariant #1 / §A) ─────────────────────────
// Unassigned-document preview + classify. The Document Review hub's Queue 1 is
// now the unassigned-DOCUMENTS inbox (GET /inbox/documents). Each doc is the
// single stored file; classify-claim turns it into a claim document, classify-bill
// spawns a bill_intake_item sharing the same stored object (single-file invariant).

// Unassigned inbox-document preview pane. Mirrors BillDocPreview but reads the
// canonical /documents/{id}/download blob (auth header required → no plain src).
function InboxDocPreview({ doc, height = 460 }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setUrl(null); setErr(false);
    if (!doc) return;
    let dead = false, obj = null;
    apiFetch(`/documents/${doc.id}/download`, { raw: true })
      .then(r => { if (!r.ok) throw new Error("doc"); return r.blob(); })
      .then(b => { if (dead) return; obj = URL.createObjectURL(b); setUrl(obj); })
      .catch(() => { if (!dead) setErr(true); });
    return () => { dead = true; if (obj) URL.revokeObjectURL(obj); };
  }, [doc?.id]);
  const frame = { border: "1px solid var(--border)", borderRadius: 8, background: "#f8fafc", height, overflow: "hidden" };
  const center = { ...frame, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text3)", fontSize: 13, textAlign: "center", padding: 16 };
  const mime = (doc && doc.mime_type) || "";
  if (!doc) return <div style={center}>No document selected.</div>;
  if (err) return <div style={center}>Couldn't load preview.<br /><a href={`/api/documents/${doc.id}/download`} target="_blank" rel="noreferrer">Open document ↗</a></div>;
  if (!url) return <Spinner label="Loading document…" />;
  if (mime.startsWith("image/")) return <div style={{ ...frame, display: "flex", alignItems: "center", justifyContent: "center" }}><img src={url} alt={doc.original_filename || "document"} style={{ maxWidth: "100%", maxHeight: height, objectFit: "contain" }} /></div>;
  if (mime === "application/pdf") return <iframe title="inbox-doc" src={url} style={{ ...frame, width: "100%", display: "block" }} />;
  return <div style={center}><a href={url} download={doc.original_filename || "document"}>Download {doc.original_filename || "document"} ↓</a></div>;
}

// Classify panel for an unassigned document: tag it to a claim (→ claim document)
// or promote it into the bill workflow (→ spawns a linked bill, optionally claim-tagged).
function ClassifyPanel({ doc, context = "ops", onClassifiedClaim, onClassifiedBill, onRouted, onRemoved }) {
  const [claimQ, setClaimQ] = useState("");
  const [claimHits, setClaimHits] = useState([]);
  const [claim, setClaim] = useState(null);
  const [working, setWorking] = useState(false);
  const [msg, setMsg] = useState(null);
  const [users, setUsers] = useState([]);
  const [routeUserId, setRouteUserId] = useState("");
  useEffect(() => { setClaim(null); setClaimQ(""); setClaimHits([]); setMsg(null); setRouteUserId(""); }, [doc?.id]);
  useEffect(() => { apiFetch("/users/assignable").then(d => setUsers(d || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!claimQ || claimQ.length < 2) { setClaimHits([]); return; }
    const t = setTimeout(() => apiFetch(`/claims?search=${encodeURIComponent(claimQ)}&limit=8`).then(d => setClaimHits(d || [])).catch(() => {}), 220);
    return () => clearTimeout(t);
  }, [claimQ]);
  const tagClaim = async () => {
    if (!claim || working) return;
    setWorking(true); setMsg(null);
    try {
      await apiFetch(`/inbox/documents/${doc.id}/classify-claim`, { method: "POST", body: { claim_id: claim.id } });
      onClassifiedClaim && onClassifiedClaim(claim);
    } catch (e) { setMsg(String(e.message || e).slice(0, 200)); } finally { setWorking(false); }
  };
  const makeBill = async () => {
    if (working) return;
    setWorking(true); setMsg(null);
    try {
      const r = await apiFetch(`/inbox/documents/${doc.id}/classify-bill`, { method: "POST", body: claim ? { claim_id: claim.id } : {} });
      onClassifiedBill && onClassifiedBill(r.bill_id);
    } catch (e) { setMsg(String(e.message || e).slice(0, 200)); } finally { setWorking(false); }
  };
  // Onward-route along the ownership axis (LD-ADJINBOX-002). Hand off to a person,
  // or (in My Inbox) return it to the Ops front door.
  const routeToUser = async () => {
    if (!routeUserId || working) return;
    setWorking(true); setMsg(null);
    try {
      await apiFetch(`/inbox/documents/${doc.id}/route`, { method: "POST", body: { to_user_id: parseInt(routeUserId) } });
      onRouted && onRouted();
    } catch (e) { setMsg(String(e.message || e).slice(0, 200)); } finally { setWorking(false); }
  };
  const returnToOps = async () => {
    if (working) return;
    setWorking(true); setMsg(null);
    try {
      await apiFetch(`/inbox/documents/${doc.id}/route`, { method: "POST", body: { to_ops: true } });
      onRouted && onRouted();
    } catch (e) { setMsg(String(e.message || e).slice(0, 200)); } finally { setWorking(false); }
  };
  // H15: the third exit from the inbox. Before this, the ONLY ways out of Queue 1
  // were "tag to claim" (files the wrong PHI onto a claim) and "this is a bill"
  // (spawns a bill) — so a mis-dropped fax, a duplicate or a personal file sat in
  // the firm-wide front door forever. Soft-delete (the row is retained; restore
  // lands with LD-REV-008), audited, and refused by the server unless you hold
  // bill-intake or the document is routed to you.
  const removeDoc = async () => {
    if (working) return;
    if (!window.confirm("Remove this document from the inbox?\n\nIt is soft-deleted — the record and the file are retained for audit, and an administrator can restore it. Use this for a mis-dropped or duplicate file.")) return;
    setWorking(true); setMsg(null);
    try {
      await apiFetch(`/documents/${doc.id}`, { method: "DELETE" });
      onRemoved && onRemoved();
    } catch (e) { setMsg(String(e.message || e).slice(0, 200)); } finally { setWorking(false); }
  };
  const SECH = { fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--text3)", margin: "9px 0 3px", borderBottom: "1px solid var(--border)", paddingBottom: 3 };
  return (
    <div style={{ fontSize: 12 }}>
      <div style={{ fontWeight: 700, color: "var(--navy)", marginBottom: 2 }}>Classify document</div>
      <div style={{ fontSize: 11.5, color: "var(--text2)", marginBottom: 8 }}>
        Tag this document to a claim, or mark it a bill. Marking a bill keeps this same file — it doesn't make a copy.
      </div>
      <div style={SECH}>Claim {claim ? "" : "(optional for a bill, required to file as a claim document)"}</div>
      {claim ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span className="mono" style={{ color: "var(--blue)", fontWeight: 700 }}>{claim.claim_number}</span>
          <span style={{ color: "var(--text2)" }}>{claim.insured_name || claim.claimant_name || ""}</span>
          <button className="btn btn-sm btn-outline" disabled={working} onClick={() => setClaim(null)}>Change</button>
        </div>
      ) : (
        <div style={{ position: "relative", marginBottom: 8 }}>
          <input value={claimQ} onChange={e => setClaimQ(e.target.value)} placeholder="Search claim # / insured / claimant…" style={inp} disabled={working} />
          {claimHits.length > 0 && (
            <div style={{ position: "absolute", zIndex: 30, background: "#fff", border: "1px solid var(--border)", borderRadius: 6, width: "100%", boxShadow: "0 6px 18px rgba(0,0,0,0.14)", maxHeight: 220, overflowY: "auto" }}>
              {claimHits.map(c => (
                <div key={c.id} onClick={() => { setClaim(c); setClaimHits([]); setClaimQ(""); }}
                  style={{ padding: "6px 10px", cursor: "pointer", borderBottom: "1px solid var(--border-light,#f0f0f0)" }}>
                  <span className="mono" style={{ color: "var(--blue)", fontWeight: 600 }}>{c.claim_number}</span>
                  <span style={{ color: "var(--text2)", marginLeft: 8 }}>{c.insured_name || c.claimant_name || ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        <button className="btn btn-blue btn-sm" disabled={working || !claim} title={claim ? "File as a claim document" : "Pick a claim first"}
          onClick={tagClaim}>{working ? "Working…" : "Tag to claim"}</button>
        <button className="btn btn-sm" style={{ border: "1px solid var(--navy)", background: "#fff", color: "var(--navy)", fontWeight: 600 }} disabled={working}
          onClick={makeBill}>This is a bill →</button>
      </div>
      <div style={SECH}>{context === "me" ? "Hand off / return" : "Route to a person"}</div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <select value={routeUserId} onChange={e => setRouteUserId(e.target.value)} style={{ ...inp, width: "auto", flex: 1, minWidth: 150 }} disabled={working}>
          <option value="">{context === "me" ? "Hand off to…" : "Choose a person…"}</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
        <button className="btn btn-sm btn-outline" disabled={working || !routeUserId} onClick={routeToUser}>Route</button>
        {context === "me" && <button className="btn btn-sm btn-outline" disabled={working} onClick={returnToOps} title="Send back to the Operations front door">Return to Ops</button>}
      </div>
      {/* Mirror the server gate (Invariant #9): bill-intake WRITE cap, or the doc
          is in your own inbox. A VIEW-only role isn't shown a button that 403s. */}
      {(context === "me" || can("manage_bill_intake")) && <>
        <div style={SECH}>Remove</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button className="btn btn-sm btn-outline" style={{ color: "#991b1b", borderColor: "#fca5a5" }} disabled={working}
            onClick={removeDoc} title="Mis-dropped, duplicate or personal file — soft-delete it out of the inbox">🗑 Remove from inbox</button>
          <span style={{ fontSize: 11, color: "var(--text3)" }}>Soft-delete — retained for audit, restorable by an administrator.</span>
        </div>
      </>}
      {msg && <div style={{ marginTop: 8, fontSize: 11.5, color: "#991b1b" }}>{msg}</div>}
    </div>
  );
}

// Compact list row for an unassigned inbox document.
function InboxDocRow({ doc, active, onClick }) {
  return (
    <div onClick={onClick} style={{ padding: "8px 10px", cursor: "pointer", borderBottom: "1px solid var(--border)", background: active ? "#eff6ff" : "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ flexShrink: 0 }}>📄</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.original_filename || "document"}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10.5, color: "var(--text3)", marginTop: 3 }}>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.3, padding: "1px 5px", borderRadius: 8, background: "var(--bg3, #e8eaf2)", color: "var(--text2)", textTransform: "uppercase" }}>
          {((doc.mime_type || "").replace("application/", "").split("/").pop() || "file")}
        </span>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.uploaded_by_name || "—"} · {doc.created_at ? fmt.date(doc.created_at) : "—"}</span>
      </div>
    </div>
  );
}

// ─── SURFACE 2: Document Review Hub (adjuster) ───────────────────────────────
// Scope E + Arc 1 §A: the adjuster/Ops review home, now document-first.
//   Queue 1 = UNASSIGNED DOCUMENTS (the unified inbox) → classify to claim or bill.
//   Queue 2 = bills still needing a claim (legacy/admin intake) → tag in BillDetail.
//   Queue 3 = my claim documents (bills routed to me) → review in BillDetail.
//   Queue 4 = future fax/email intake. Deep-linkable via focusBillId (a bill).
const MN_HDR = { fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4, margin: "0 0 4px 2px" };
export function BillReviewQueue({ onNavigateToClaim, focusBillId }) {
  const [docs, setDocs] = useState([]);            // unassigned inbox documents (Queue 1)
  const [bills, setBills] = useState([]);          // /me/bill-review (claim bills + legacy claimless)
  // Typed selection so a doc id and a bill id never collide: { type:'doc'|'bill', id }.
  const [sel, setSel] = useState(focusBillId ? { type: "bill", id: focusBillId } : null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const fileRef = useRef(null);
  const load = useCallback(() => {
    apiFetch("/inbox/documents").then(d => setDocs(d || [])).catch(() => setDocs([]));
    apiFetch("/me/bill-review").then(d => setBills(d || [])).catch(() => setBills([]));
  }, []);
  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => { if (focusBillId) setSel({ type: "bill", id: focusBillId }); }, [focusBillId]);
  const refresh = () => setTick(t => t + 1);

  const unassignedBills = bills.filter(b => !b.claim_id);   // legacy/admin claimless bills
  const claimBills = bills.filter(b => b.claim_id);
  const selDoc = sel?.type === "doc" ? docs.find(d => d.id === sel.id) : null;

  // If the selected document leaves the inbox (after classify), drop the selection.
  useEffect(() => {
    if (sel?.type === "doc" && !docs.some(d => d.id === sel.id)) setSel(null);
  }, [docs, sel]);

  // Document-first intake: a drop/pick uploads to the unassigned inbox and selects it.
  const uploadDoc = async (file) => {
    if (!file || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiFetch("/inbox/documents", { raw: true, method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      const d = await res.json();
      refresh();
      setSel({ type: "doc", id: d.id });
    } catch (e) { window.alert("Upload failed: " + String(e.message || e).slice(0, 200)); }
    finally { setBusy(false); }
  };

  const onClassifiedClaim = () => { refresh(); setSel(null); };
  const onClassifiedBill = (billId) => { refresh(); if (billId) setSel({ type: "bill", id: billId }); };

  const billSections = [
    { key: "unassignedBills", label: "Bills needing a claim", rows: unassignedBills },
    { key: "claim", label: "My claim documents", rows: claimBills },
  ];
  const nothing = docs.length === 0 && bills.length === 0;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.webp,.gif" style={{ display: "none" }}
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadDoc(f); }} />
        <button className="btn btn-blue btn-sm" style={{ marginLeft: "auto" }} disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? "Uploading…" : "+ Add document"}</button>
      </div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ width: 320, flexShrink: 0 }}>
          {/* Queue 1 — unassigned documents (the unified inbox) */}
          <div style={{ marginBottom: 12 }}>
            <div style={MN_HDR}>Unassigned — needs classifying ({docs.length})</div>
            {docs.length === 0
              ? <div style={{ border: "1px dashed var(--border)", borderRadius: 8, padding: "10px 12px", fontSize: 11.5, color: "var(--text3)" }}>No unclassified documents. Drop one with “+ Add document”.</div>
              : <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
                  {docs.map(d => <InboxDocRow key={d.id} doc={d} active={sel?.type === "doc" && sel.id === d.id} onClick={() => setSel({ type: "doc", id: d.id })} />)}
                </div>}
          </div>
          {/* Queues 2 & 3 — bills (legacy claimless + my claim documents) */}
          {billSections.map(s => s.rows.length === 0 ? null : (
            <div key={s.key} style={{ marginBottom: 12 }}>
              <div style={MN_HDR}>{s.label} ({s.rows.length})</div>
              <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
                {s.rows.map(b => <QueueRow key={b.id} b={b} active={sel?.type === "bill" && sel.id === b.id} showReviewer onClick={() => setSel({ type: "bill", id: b.id })} />)}
              </div>
            </div>
          ))}
          {/* Queue 4 — future fax / email intake */}
          <div style={{ marginTop: 4 }}>
            <div style={MN_HDR}>Fax / email intake</div>
            <div style={{ border: "1px dashed var(--border)", borderRadius: 8, padding: "10px 12px", fontSize: 11.5, color: "var(--text3)" }}>
              Inbound fax &amp; email documents will land here as unassigned documents — ships with document ingestion.
            </div>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {selDoc ? (
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}><InboxDocPreview doc={selDoc} /></div>
              <div style={{ width: 320, flexShrink: 0, border: "1px solid var(--border)", borderRadius: 8, padding: 12, background: "#fff" }}>
                <ClassifyPanel doc={selDoc} context="ops" onClassifiedClaim={onClassifiedClaim} onClassifiedBill={onClassifiedBill} onRouted={onClassifiedClaim} onRemoved={onClassifiedClaim} />
              </div>
            </div>
          ) : sel?.type === "bill" ? (
            <BillDetail billId={sel.id} surface="review" onChanged={refresh} />
          ) : (
            <IntakeDropZone busy={busy} prominent={nothing} onPick={() => fileRef.current?.click()} onFile={uploadDoc}
              title="Drop a document to start" subtitle="PDF or image — it opens here to classify (tag a claim, or mark it a bill)." />
          )}
        </div>
      </div>
      {nothing && <div style={{ marginTop: 10, fontSize: 12, color: "var(--text3)", textAlign: "center" }}>Nothing in the inbox or your review queue right now.</div>}
    </div>
  );
}

// ─── SURFACE 2b: My Inbox (ADJUSTER-INBOX-1) ─────────────────────────────────
// The adjuster's self-scoped personal intake — a lens over the SAME inbox engine
// (LD-ADJINBOX-001/003). Queue 1 = documents routed to ME with no claim yet
// (GET /me/inbox); Queue 2 = bills needing MY review (/me/bill-review); Queue 3 =
// my fax intake (placeholder). NEVER shows the firm-wide front door — that lives in
// the Ops Document Review hub. Classify (tag claim / mark bill) or route onward
// (hand off to a person / return to Ops) via the shared ClassifyPanel (context="me").
export function MyInbox({ onWorkChanged }) {
  const [docs, setDocs] = useState([]);
  const [bills, setBills] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const fileRef = useRef(null);
  const load = useCallback(() => {
    apiFetch("/me/inbox").then(d => setDocs(d || [])).catch(() => setDocs([]));
    apiFetch("/me/bill-review").then(d => setBills(d || [])).catch(() => setBills([]));
  }, []);
  useEffect(() => { load(); }, [load, tick]);
  const refresh = () => setTick(t => t + 1);
  const selDoc = sel?.type === "doc" ? docs.find(d => d.id === sel.id) : null;
  useEffect(() => { if (sel?.type === "doc" && !docs.some(d => d.id === sel.id)) setSel(null); }, [docs, sel]);

  // Drop a doc straight into my own inbox (self-scoped upload; no Ops cap needed).
  const uploadToMe = async (file) => {
    if (!file || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiFetch("/me/inbox/documents", { raw: true, method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      const d = await res.json();
      refresh();
      setSel({ type: "doc", id: d.id });
    } catch (e) { window.alert("Upload failed: " + String(e.message || e).slice(0, 200)); }
    finally { setBusy(false); }
  };

  const onDone = () => { refresh(); setSel(null); onWorkChanged && onWorkChanged(); };
  const onClassifiedBill = (billId) => { refresh(); if (billId) setSel({ type: "bill", id: billId }); onWorkChanged && onWorkChanged(); };
  const nothing = docs.length === 0 && bills.length === 0;

  return (
    <div className="page">
      <PageHeader title="My Inbox" subtitle="Documents routed to you, your reviews, and your faxes — in one place."
        actions={<>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.webp,.gif" style={{ display: "none" }}
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadToMe(f); }} />
          <button className="btn btn-blue btn-sm" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? "Uploading…" : "+ Add document"}</button>
        </>} />
      {/* UI-RIBBON-1 (LD-DESIGN-010): inbox vitals from the lists this page already loads. */}
      <SummaryRibbon>
        {[
          <RibbonCell key="r" icon="📥" label="Routed to Me" value={String(docs.length)} color={docs.length > 0 ? "var(--navy)" : undefined} />,
          <RibbonCell key="b" icon="🧾" label="My Bill Reviews" value={String(bills.length)} />,
        ]}
      </SummaryRibbon>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ width: 320, flexShrink: 0 }}>
          <div style={{ marginBottom: 12 }}>
            <div style={MN_HDR}>Routed to me — needs action ({docs.length})</div>
            {docs.length === 0
              ? <div style={{ border: "1px dashed var(--border)", borderRadius: 8, padding: "10px 12px", fontSize: 11.5, color: "var(--text3)" }}>Nothing routed to you. Drop one with “+ Add document”.</div>
              : <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
                  {docs.map(d => <InboxDocRow key={d.id} doc={d} active={sel?.type === "doc" && sel.id === d.id} onClick={() => setSel({ type: "doc", id: d.id })} />)}
                </div>}
          </div>
          {bills.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={MN_HDR}>Needs my review ({bills.length})</div>
              <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
                {bills.map(b => <QueueRow key={b.id} b={b} active={sel?.type === "bill" && sel.id === b.id} showReviewer onClick={() => setSel({ type: "bill", id: b.id })} />)}
              </div>
            </div>
          )}
          <div style={{ marginTop: 4 }}>
            <div style={MN_HDR}>My fax intake</div>
            <div style={{ border: "1px dashed var(--border)", borderRadius: 8, padding: "10px 12px", fontSize: 11.5, color: "var(--text3)" }}>
              Faxes sent to your personal line will land here — ships with document ingestion.
            </div>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {selDoc ? (
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}><InboxDocPreview doc={selDoc} /></div>
              <div style={{ width: 320, flexShrink: 0, border: "1px solid var(--border)", borderRadius: 8, padding: 12, background: "#fff" }}>
                <ClassifyPanel doc={selDoc} context="me" onClassifiedClaim={onDone} onClassifiedBill={onClassifiedBill} onRouted={onDone} onRemoved={onDone} />
              </div>
            </div>
          ) : sel?.type === "bill" ? (
            <BillDetail billId={sel.id} surface="review" onChanged={refresh} />
          ) : (
            <IntakeDropZone busy={busy} prominent={nothing} onPick={() => fileRef.current?.click()} onFile={uploadToMe}
              title="Drop a document into your inbox" subtitle="PDF or image — it opens here to tag a claim, mark a bill, or route onward." />
          )}
        </div>
      </div>
      {nothing && <div style={{ marginTop: 10, fontSize: 12, color: "var(--text3)", textAlign: "center" }}>Your inbox is clear. 🎉</div>}
    </div>
  );
}

// ─── SURFACE 3: Claim-level Bill Panel ───────────────────────────────────────
export function ClaimBillsTab({ claim, focusBillId, onWorkChanged }) {
  const claimId = claim?.id;
  const [bills, setBills] = useState([]);
  const [sel, setSel] = useState(focusBillId || null);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [trustCfg, setTrustCfg] = useState(null);
  const [trustAccts, setTrustAccts] = useState([]);
  const load = useCallback(() => { if (claimId) apiFetch(`/claims/${claimId}/bills`).then(d => setBills(d || [])).catch(() => setBills([])); }, [claimId]);
  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => { if (focusBillId) setSel(focusBillId); }, [focusBillId]);
  // Claim's default trust account — trust-funded bills inherit it; Accounting confirms
  // the actual pay-from account at payment. Adjusters only choose Trust/Underwriter/Insured,
  // never the specific account, so this control is gated to bill-intake managers.
  useEffect(() => {
    if (!claimId) return;
    apiFetch(`/claims/${claimId}/trust-config`).then(setTrustCfg).catch(() => setTrustCfg(null));
    apiFetch("/trust-accounts").then(d => setTrustAccts(d || [])).catch(() => {});
  }, [claimId]);
  const setDefaultTrust = (val) => {
    const body = val ? { trust_mode: "individual", trust_account_id: parseInt(val) } : { trust_mode: "none", trust_account_id: null };
    apiFetch(`/claims/${claimId}/trust-config`, { method: "PATCH", body })
      .then(() => apiFetch(`/claims/${claimId}/trust-config`).then(setTrustCfg))
      .catch(e => window.alert(String(e.message || e).slice(0, 160)));
  };
  // Bump app-wide work-refresh too, so the claim Attention panel + header bell
  // live-update when a transition opens/resolves a PAYMENT_REVIEW item.
  const refresh = () => { setTick(t => t + 1); onWorkChanged && onWorkChanged(); };
  // Drop/pick a bill straight onto this claim — same flow as the Ops intake and
  // My Inbox: upload, then open it right here in BillDetail/CodingPanel, which
  // carries the full payee search + autofill. One pattern everywhere a doc loads,
  // not a stripped-down claim-only modal.
  const uploadFile = async (file) => {
    if (!file || busy || !claimId) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("claim_id", String(claimId));
      fd.append("source", "claim_submit");
      const res = await apiFetch("/bills", { raw: true, method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      const b = await res.json();
      refresh();
      setSel(b.id);
    } catch (e) { window.alert("Upload failed: " + String(e.message || e).slice(0, 200)); }
    finally { setBusy(false); }
  };
  const groups = [
    { label: "In progress", test: b => !["Paid", "Rejected"].includes(b.current_status) },
    { label: "Paid", test: b => b.current_status === "Paid" },
    { label: "Rejected", test: b => b.current_status === "Rejected" },
  ];
  return (
    <div>
      <div className="section-header" style={{ display: "flex", alignItems: "center" }}>
        <span className="section-title">Bills</span>
        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.webp,.gif" style={{ display: "none" }}
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadFile(f); }} />
        <button className="btn btn-blue btn-sm" style={{ marginLeft: "auto" }} disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? "Uploading…" : "+ Add Bill"}</button>
      </div>
      {can("manage_bill_intake") && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "8px 0 2px", fontSize: 12, flexWrap: "wrap" }}>
          <span style={{ color: "var(--text3)" }}>Default trust account:</span>
          <select value={trustCfg?.trust_mode === "individual" ? (trustCfg.trust_account_id || "") : ""}
            onChange={e => setDefaultTrust(e.target.value)} style={{ fontSize: 12, padding: "3px 7px", border: "1px solid var(--border)", borderRadius: 5 }}>
            <option value="">— None —</option>
            {trustAccts.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {trustCfg?.trust_mode === "general" && <span style={{ fontSize: 11, color: "var(--text3)" }}>(currently using the insured's general trust)</span>}
          <span style={{ fontSize: 10.5, color: "var(--text3)" }}>Trust-funded bills inherit this; Accounting confirms the pay-from account at payment.</span>
        </div>
      )}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", marginTop: 10 }}>
        <div style={{ width: 300, flexShrink: 0 }}>
          {bills.length === 0 ? <div style={{ padding: 20, color: "var(--text3)", fontSize: 13, border: "1px solid var(--border)", borderRadius: 8, textAlign: "center" }}>No bills on this claim yet.</div>
            : groups.map(g => {
              const rows = bills.filter(g.test);
              if (!rows.length) return null;
              return (
                <div key={g.label} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4, margin: "0 0 4px 2px" }}>{g.label} ({rows.length})</div>
                  <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
                    {rows.map(b => <QueueRow key={b.id} b={b} active={sel === b.id} showReviewer hideClaim onClick={() => setSel(b.id)} />)}
                  </div>
                </div>
              );
            })}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {sel ? <BillDetail billId={sel} surface="claim" claim={claim} onChanged={refresh} />
            : <IntakeDropZone busy={busy} prominent={bills.length === 0} onPick={() => fileRef.current?.click()} onFile={uploadFile} />}
        </div>
      </div>
    </div>
  );
}

// ─── shared styles ───────────────────────────────────────────────────────────
const lbl = { display: "block", fontSize: 10.5, fontWeight: 600, color: "var(--text3)", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.3 };
const inp = { width: "100%", fontSize: 12, padding: "5px 8px", border: "1px solid var(--border)", borderRadius: 5, fontFamily: "inherit", boxSizing: "border-box" };
const chip = { fontSize: 11.5, fontWeight: 600, padding: "4px 10px", borderRadius: 16, border: "1px solid var(--border)", background: "#fff", color: "var(--text2)", cursor: "pointer" };
const chipActive = { background: "var(--navy)", color: "#fff", borderColor: "var(--navy)" };
const modalWrap = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "8vh", zIndex: 2000 };
const modalBox = { background: "#fff", borderRadius: 12, padding: 22, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", maxHeight: "84vh", overflowY: "auto" };
